import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Row,
  Col,
  message,
  Statistic,
  DatePicker,
  Icon,
  Switch,
  Tag,
  Divider,
  Modal,
  Spin
} from 'antd';
import moment from 'moment';
import { loadCSV, parseStandardRate } from './utils/csvLoader';
import Papa from 'papaparse';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchDailyFuelDataReport, fetchFullTankEventsForPeriod, findAssetIdByCode, fetchFuelEntries } from './utils/fuelService';
import { apiGet } from './utils/apiService';
import { getUserData } from './utils/authService';

const { Option } = Select;

// Helper function to extract calculated reading from mileage
// Prefers calculatedReading from meterReadingRecord (cumulative, always increasing)
// Falls back to reading (physical meter reading, may reset on meter change)
const getCalculatedReading = (mileage) => {
  if (!mileage) return null;
  // Check for calculatedReading in meterReadingRecord first
  if (mileage.meterReadingRecord && 
      mileage.meterReadingRecord.calculatedReading !== null && 
      mileage.meterReadingRecord.calculatedReading !== undefined) {
    return mileage.meterReadingRecord.calculatedReading;
  }
  // Fall back to regular reading
  return mileage.reading;
};

// Helper function to get both readings for display purposes
const getMeterReadings = (mileage) => {
  if (!mileage) return { reading: null, calculatedReading: null };
  const reading = mileage.reading;
  const calculatedReading = mileage.meterReadingRecord?.calculatedReading ?? null;
  return { reading, calculatedReading };
};

// Comparison Table Component
const ComparisonTable = ({ data, selectedPeriods, loading, filters, columns, selectedMonth }) => {
  // Filter data based on filters
  let filtered = data;
  if (filters.assetCode) {
    filtered = filtered.filter(item => item.assetCode === filters.assetCode);
  }
  if (filters.assetType) {
    filtered = filtered.filter(item => item.assetType === filters.assetType);
  }
  if (filters.assetSubType) {
    filtered = filtered.filter(item => item.assetSubType === filters.assetSubType);
  }
  if (filters.assetModel) {
    filtered = filtered.filter(item => item.assetModel === filters.assetModel);
  }
  if (filters.location) {
    filtered = filtered.filter(item => {
      // Check if any of the selected periods have matching location
      return selectedPeriods.some(period => {
        const periodKey = period.format('YYYY-MM');
        const periodData = item.periods[periodKey];
        return periodData && periodData.location === filters.location;
      });
    });
  }
  if (filters.subLocation) {
    filtered = filtered.filter(item => {
      // Check if any of the selected periods have matching sub location
      return selectedPeriods.some(period => {
        const periodKey = period.format('YYYY-MM');
        const periodData = item.periods[periodKey];
        return periodData && periodData.subLocation === filters.subLocation;
      });
    });
  }

  // Transform data to show periods as rows
  const comparisonRows = [];
  filtered.forEach(asset => {
    selectedPeriods.forEach(period => {
      const periodKey = period.format('YYYY-MM');
      const periodData = asset.periods[periodKey];
      
      if (periodData) {
        comparisonRows.push({
          ...periodData,
          periodLabel: period.format('MMM YYYY'),
          periodKey: periodKey,
          periodMoment: period, // Store moment object for month calculation
          assetGroupKey: `${asset.assetCode}_${asset.assetModel}`,
          // Keep asset info for grouping
          _assetCode: asset.assetCode,
          _assetType: asset.assetType,
          _assetSubType: asset.assetSubType,
          _assetModel: asset.assetModel,
          _ownerCode: asset.ownerCode,
          _altName: asset.altName
        });
      }
    });
  });

  // Add period column at the beginning, then reorder to match main table
  const comparisonColumns = [
    {
      title: 'Period',
      dataIndex: 'periodLabel',
      key: 'periodLabel',
      width: 100,
      fixed: 'left',
      render: (text, record) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>{text}</Tag>
      )
    },
    ...columns.filter(col => 
      col.key !== 'snNo' && // Remove SN No. as we'll use period instead
      col.dataIndex !== 'snNo'
    ).map(col => ({
      ...col,
      // Keep fixed property as is (Asset Code, Asset Type, etc. should remain fixed)
      fixed: col.fixed === 'left' ? 'left' : undefined
    }))
  ];

  // Add row grouping styling
  const getRowClassName = (record, index) => {
    if (index > 0) {
      const prevRecord = comparisonRows[index - 1];
      if (prevRecord && prevRecord.assetGroupKey === record.assetGroupKey) {
        return 'comparison-row-grouped';
      }
    }
    return 'comparison-row-first';
  };

  return (
    <div>
      <style>{`
        .comparison-row-grouped {
          background-color: #fafafa;
        }
        .comparison-row-first {
          border-top: 2px solid #1890ff;
        }
        .comparison-row-grouped td:first-child {
          border-left: 3px solid #1890ff;
        }
      `}</style>
      <Table
        columns={comparisonColumns}
        dataSource={comparisonRows}
        rowKey={(record) => `${record.assetGroupKey}_${record.periodKey}`}
        loading={loading}
        scroll={{ x: 2000, y: 600 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} rows`
        }}
        rowClassName={getRowClassName}
      />
    </div>
  );
};

// Transform API response to CSV-like format expected by aggregateData
// fuelBowserMap: Optional map of asset codes to isBowser status
// assetTypeMap: Optional map of asset type IDs to names
// Filters out assets where asset.equipmentSubType.assetType.isBowser === true
const transformApiResponseToCsvFormat = (apiData, fuelBowserMap = {}, assetTypeMap = {}) => {
  if (!apiData || !Array.isArray(apiData)) {
    return [];
  }

  // Filter out assets where asset type has "isBowser" = True
  // Check nested path: asset.equipmentSubType.assetType.isBowser
  const filteredData = apiData.filter((dailyRecord) => {
    const asset = dailyRecord.asset || {};
    const assetCode = asset.assetCode || '';
    
    // First check the fuelBowserMap (from asset list API)
    if (fuelBowserMap[assetCode] === true) {
      console.log(`Filtering out fuel bowser asset from map: ${assetCode}`);
      return false;
    }
    
    // Check nested structure: asset.equipmentSubType.assetType.isBowser
    let isBowser = false;
    
    // Check equipmentSubType.assetType.isBowser (primary path based on API structure)
    if (asset.equipmentSubType && typeof asset.equipmentSubType === 'object' && asset.equipmentSubType !== null) {
      const assetType = asset.equipmentSubType.assetType;
      if (assetType && typeof assetType === 'object' && assetType !== null) {
        isBowser = assetType.isBowser === true || assetType.isBowser === 'true';
      }
    }
    
    // Fallback: Check asset.assetType directly (for backward compatibility)
    if (!isBowser && asset.assetType && typeof asset.assetType === 'object' && asset.assetType !== null) {
      isBowser = asset.assetType.isBowser === true || 
                 asset.assetType.isBowser === 'true' ||
                 asset.assetType.isFuelBowser === true ||
                 asset.assetType.isAFuelBowser === true ||
                 asset.assetType['Is a fuel Bowser'] === true ||
                 false;
    }
    
    // Exclude if isBowser is true
    if (isBowser) {
      console.log(`Filtering out bowser asset (isBowser=true): ${assetCode}`);
      return false;
    }
    
    return true;
  });
  
  const filteredCount = apiData.length - filteredData.length;
  if (filteredCount > 0) {
    console.log(`Filtered out ${filteredCount} fuel bowser asset(s) from the report`);
  }

  return filteredData.map((dailyRecord) => {
    const asset = dailyRecord.asset || {};
    
    // Handle nested location object
    const locationObj = asset.location || {};
    const locationName = typeof locationObj === 'string' ? locationObj : (locationObj.name || locationObj.code || '');
    
    // Handle nested subLocation object
    const subLocationObj = asset.subLocation || {};
    const subLocationName = typeof subLocationObj === 'string' ? subLocationObj : (subLocationObj.name || subLocationObj.code || '');
    
    // Handle nested assetModel object
    const assetModelObj = asset.assetModel || {};
    const assetModelName = typeof assetModelObj === 'string' ? assetModelObj : (assetModelObj.name || '');
    
    // Handle nested equipmentSubType (Asset Sub Type)
    const equipmentSubTypeObj = asset.equipmentSubType || {};
    const assetSubTypeName = typeof equipmentSubTypeObj === 'string' ? equipmentSubTypeObj : (equipmentSubTypeObj.name || '');
    
    // Extract Asset Type name from nested structure
    // Check equipmentSubType.assetType first (primary path)
    let assetTypeName = '';
    let assetTypeId = null;
    
    if (equipmentSubTypeObj && typeof equipmentSubTypeObj === 'object' && equipmentSubTypeObj !== null) {
      const assetTypeObj = equipmentSubTypeObj.assetType;
      if (assetTypeObj) {
        if (typeof assetTypeObj === 'object' && assetTypeObj !== null) {
          assetTypeId = assetTypeObj._id || assetTypeObj.id;
          assetTypeName = assetTypeObj.name || '';
        } else if (typeof assetTypeObj === 'string') {
          assetTypeId = assetTypeObj;
        }
      }
    }
    
    // Fallback: Check asset.assetType directly
    if (!assetTypeName && !assetTypeId && asset.assetType) {
      if (typeof asset.assetType === 'object' && asset.assetType !== null) {
        assetTypeId = asset.assetType._id || asset.assetType.id;
        assetTypeName = asset.assetType.name || '';
      } else if (typeof asset.assetType === 'string') {
        assetTypeId = asset.assetType;
      }
    }
    
    // Look up asset type name from map if we have an ID but no name
    if (assetTypeId && !assetTypeName && assetTypeMap[assetTypeId]) {
      assetTypeName = assetTypeMap[assetTypeId];
    }
    
    // Get date from date_timestamp or date field
    let date = '';
    if (dailyRecord.date_timestamp) {
      date = moment(dailyRecord.date_timestamp).format('DD/MM/YYYY');
    } else if (dailyRecord.date) {
      date = moment(dailyRecord.date).format('DD/MM/YYYY');
    }

    // Convert milliseconds to minutes
    const ignitionTimeMin = dailyRecord.ignition_time ? Math.round(dailyRecord.ignition_time / (1000 * 60)) : 0;
    const idleTimeMin = dailyRecord.ideal_time || dailyRecord.idle_time ? Math.round((dailyRecord.ideal_time || dailyRecord.idle_time) / (1000 * 60)) : 0;
    const movingTimeMin = dailyRecord.moving_time ? Math.round(dailyRecord.moving_time / (1000 * 60)) : 0;
    
    // Convert distance from meters to kilometers
    const distanceKm = dailyRecord.distance_travelled ? (dailyRecord.distance_travelled / 1000) : 0;
    
    // Fuel values - use fuel_consumed (processed) or fuel_consumed_analog (raw)
    const fuelConsumed = dailyRecord.fuel_consumed || dailyRecord.fuel_consumed_analog || 0;
    // total_refuel is negative, convert to positive for "Possible Refuel"
    const possibleRefuel = dailyRecord.total_refuel ? Math.abs(dailyRecord.total_refuel) : 0;
    const possibleDrain = dailyRecord.total_theft || 0;
    
    // Extract manual refuel quantity from manualFuelData
    const manualRefuelQuantity = dailyRecord.manualFuelData?.quantity || 0;
    
    // Data available in hours - calculate from actual data collection period
    // Use dataFromTimestamp and dataToTimestamp if available, otherwise fall back to startOfDayEpoch/endOfDayEpoch
    // This represents the time period for which data was collected, not just ignition time
    let dataAvailableHours = 0;
    if (dailyRecord.dataFromTimestamp && dailyRecord.dataToTimestamp) {
      // Calculate from actual data collection timestamps
      const dataCollectionDurationMs = dailyRecord.dataToTimestamp - dailyRecord.dataFromTimestamp;
      dataAvailableHours = dataCollectionDurationMs / (1000 * 60 * 60); // Convert ms to hours
    } else if (dailyRecord.startOfDayEpoch && dailyRecord.endOfDayEpoch) {
      // Fallback to day boundaries if data timestamps not available
      const dayDurationMs = dailyRecord.endOfDayEpoch - dailyRecord.startOfDayEpoch;
      dataAvailableHours = dayDurationMs / (1000 * 60 * 60); // Convert ms to hours
    } else {
      // Last resort: use ignition_time (but this is incorrect - should be fixed in API)
      // This is a fallback for backward compatibility
      dataAvailableHours = dailyRecord.ignition_time ? (dailyRecord.ignition_time / (1000 * 60 * 60)) : 0;
    }

    return {
      'Date': date,
      'Asset Code': asset.assetCode || '',
      'Asset Type': assetTypeName || '',
      'Asset Sub Type': assetSubTypeName,
      'Asset Model': assetModelName,
      'Owner Code': asset.ownerCode || '',
      'Alt Name': asset.altName || '',
      'Location': locationName,
      'Sub Location': subLocationName,
      'Ignition Time (min)': ignitionTimeMin,
      'Idle Time (min)': idleTimeMin,
      'Moving Time (min)': movingTimeMin,
      'Distance Travelled (km)': distanceKm,
      'Fuel Consumed (ltrs)': fuelConsumed,
      'Possible Refuel': possibleRefuel,
      'Possible Drain': possibleDrain,
      'Manual Refuel Quantity': manualRefuelQuantity,
      'Data Available (hours)': dataAvailableHours,
      // Store original API record for reference
      _apiRecord: dailyRecord
    };
  });
};

function IoTSummaryReport() {
  const [loading, setLoading] = useState(false);
  const lastLoadParamsRef = React.useRef(null); // Track last load parameters to prevent duplicate loads
  const [fuelBowserMap, setFuelBowserMap] = useState({}); // Map of asset codes to isFuelBowser status
  const [assetTypeMap, setAssetTypeMap] = useState({}); // Map of asset type IDs to names
  const [summaryData, setSummaryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [assetModels, setAssetModels] = useState([]);
  const [assetCodes, setAssetCodes] = useState([]);
  const [assetCodeMap, setAssetCodeMap] = useState({}); // Map for asset code search (includes owner code and alt name)
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [selectedAssetSubType, setSelectedAssetSubType] = useState(null);
  const [selectedAssetModel, setSelectedAssetModel] = useState(null);
  const [selectedAssetCode, setSelectedAssetCode] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(moment('2025-11', 'YYYY-MM'));
  const [standardRatesMap, setStandardRatesMap] = useState({});
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedPeriods, setSelectedPeriods] = useState([moment('2025-11', 'YYYY-MM')]);
  const [comparisonData, setComparisonData] = useState([]);
  const [fullTankDataMap, setFullTankDataMap] = useState({});
  const [fuelAddedMap, setFuelAddedMap] = useState({}); // Map for total fuel added from consumption report (all events)
  const [fullTankDataLoaded, setFullTankDataLoaded] = useState(false);
  const [fuelAddedDataLoaded, setFuelAddedDataLoaded] = useState(false);
  const [dailyData, setDailyData] = useState([]); // Store original daily CSV data
  const [fullTankRawData, setFullTankRawData] = useState([]); // Store original full tank CSV data
  const [fileNames, setFileNames] = useState({
    iotDeviceReport: null,
    consumptionReport: null,
    assetModelList: null
  });
  const [dailyBreakdownVisible, setDailyBreakdownVisible] = useState(false);
  const [fullTankBreakdownVisible, setFullTankBreakdownVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dailyBreakdownData, setDailyBreakdownData] = useState([]);
  const [fullTankBreakdownData, setFullTankBreakdownData] = useState([]);

  // Discover files from IOTReportData folder by prefix patterns
  const discoverFiles = async () => {
    try {
      // First, try to fetch a manifest file (optional override)
      try {
        const manifestResponse = await fetch('/IOTReportData/manifest.json', {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' }
        });
        
        if (manifestResponse.ok) {
          const manifest = await manifestResponse.json();
          const discovered = {
            iotDeviceReport: manifest.iotDeviceReport || null,
            consumptionReport: manifest.consumptionReport || null,
            assetModelList: manifest.assetModelList || null
          };
          
          // Verify files actually exist before using manifest
          const verified = await verifyFilesExist(discovered, true); // silent verification
          if (verified.allExist) {
            setFileNames(discovered);
            console.log('✓ Loaded and verified file names from manifest:', discovered);
            return;
          } else {
            console.warn('Manifest files not found, will try auto-discovery:', verified.missing);
          }
        }
      } catch (manifestError) {
        console.log('Manifest file not found, auto-discovering files by prefix...');
      }

      // Auto-discover files by trying to fetch directory listing or parse HTML directory listing
      console.log('Attempting to auto-discover files by prefix patterns...');
      const discovered = await discoverFilesByPrefix();
      
      console.log('Discovery result:', discovered);
      
      if (discovered.iotDeviceReport || discovered.consumptionReport || discovered.assetModelList) {
        console.log('Setting discovered file names:', discovered);
        setFileNames(discovered);
        console.log('Auto-discovered files:', discovered);
        await verifyFilesExist(discovered);
      } else {
        console.error('Could not auto-discover files. Please ensure files are in IOTReportData folder.');
        console.error('Tried to find files with prefixes:');
        console.error('  - iot-device-report-*.csv');
        console.error('  - consumption-report-*.csv');
        console.error('  - Asset_Model_List_*.csv');
        message.error('Could not discover report files. Please check browser console for details.');
      }
      
    } catch (error) {
      console.error('Error discovering files:', error);
      console.error('Error stack:', error.stack);
      message.error('Could not discover report files. Please ensure files are in IOTReportData folder.');
    }
  };

  // Verify that discovered files actually exist by attempting to fetch them
  const verifyFilesExist = async (fileNames, silent = false) => {
    const missing = [];
    const results = {
      allExist: true,
      missing: []
    };
    
    if (fileNames.iotDeviceReport) {
      try {
        const test = await fetch(`/IOTReportData/${fileNames.iotDeviceReport}`, { method: 'HEAD', cache: 'no-cache' });
        if (!test.ok) {
          missing.push(`IoT Device Report: ${fileNames.iotDeviceReport}`);
          results.allExist = false;
        } else {
          console.log(`✓ Verified IoT Device Report exists: ${fileNames.iotDeviceReport}`);
        }
      } catch (e) {
        missing.push(`IoT Device Report: ${fileNames.iotDeviceReport} - ${e.message}`);
        results.allExist = false;
      }
    }
    
    if (fileNames.consumptionReport) {
      try {
        const test = await fetch(`/IOTReportData/${fileNames.consumptionReport}`, { method: 'HEAD', cache: 'no-cache' });
        if (!test.ok) {
          missing.push(`Consumption Report: ${fileNames.consumptionReport}`);
          results.allExist = false;
        } else {
          console.log(`✓ Verified Consumption Report exists: ${fileNames.consumptionReport}`);
        }
      } catch (e) {
        missing.push(`Consumption Report: ${fileNames.consumptionReport} - ${e.message}`);
        results.allExist = false;
      }
    }
    
    if (fileNames.assetModelList) {
      try {
        const test = await fetch(`/IOTReportData/${fileNames.assetModelList}`, { method: 'HEAD', cache: 'no-cache' });
        if (!test.ok) {
          missing.push(`Asset Model List: ${fileNames.assetModelList}`);
          results.allExist = false;
        } else {
          console.log(`✓ Verified Asset Model List exists: ${fileNames.assetModelList}`);
        }
      } catch (e) {
        missing.push(`Asset Model List: ${fileNames.assetModelList} - ${e.message}`);
        results.allExist = false;
      }
    }
    
    results.missing = missing;
    
    if (missing.length > 0 && !silent) {
      console.warn('Some files could not be found:', missing);
      message.warning(`Some files could not be found: ${missing.join(', ')}`);
    }
    
    return results;
  };

  // Discover files by prefix patterns - tries multiple methods
  const discoverFilesByPrefix = async () => {
    const discovered = {
      iotDeviceReport: null,
      consumptionReport: null,
      assetModelList: null
    };

    // Method 1: Try to fetch directory listing HTML and parse it
    try {
      const dirResponse = await fetch('/IOTReportData/', {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (dirResponse.ok) {
        const html = await dirResponse.text();
        console.log('Directory listing HTML received, length:', html.length);
        
        // Parse HTML directory listing to extract file names
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Try multiple selectors for different directory listing formats
        let links = Array.from(doc.querySelectorAll('a'));
        if (links.length === 0) {
          // Try alternative: might be in a list or table
          links = Array.from(doc.querySelectorAll('li a, tr td a, .file a'));
        }
        
        console.log('Found links in directory listing:', links.length);
        
        const files = links
          .map(link => {
            const href = link.getAttribute('href');
            const text = link.textContent.trim();
            const name = href || text;
            // Remove query strings, fragments, and path components
            return name.split('?')[0].split('#')[0].replace(/^.*\//, '').replace(/^.*\\/, '');
          })
          .filter(name => name && name.endsWith('.csv') && !name.includes('manifest') && name.length > 0)
          .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
        
        console.log('Extracted CSV files from directory listing:', files);
        
        // Match by prefix
        discovered.iotDeviceReport = files.find(f => f.startsWith('iot-device-report-') && f.endsWith('.csv')) || null;
        discovered.consumptionReport = files.find(f => f.startsWith('consumption-report-') && f.endsWith('.csv')) || null;
        discovered.assetModelList = files.find(f => f.startsWith('Asset_Model_List_') && f.endsWith('.csv')) || null;
        
        console.log('Matched files:', {
          iotDeviceReport: discovered.iotDeviceReport,
          consumptionReport: discovered.consumptionReport,
          assetModelList: discovered.assetModelList
        });
        
        if (discovered.iotDeviceReport || discovered.consumptionReport || discovered.assetModelList) {
          console.log('✓ Successfully discovered files from directory listing');
          return discovered;
        } else {
          console.warn('Directory listing found files but none matched the expected prefixes:', files);
        }
      } else {
        console.log(`Directory listing returned status: ${dirResponse.status}`);
      }
    } catch (dirError) {
      console.log('Directory listing not available:', dirError.message);
      console.log('Trying alternative methods...');
    }

    // Method 2: Try server endpoint that lists files (JSON array)
    try {
      const listResponse = await fetch('/IOTReportData/list', {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (listResponse.ok) {
        const files = await listResponse.json();
        console.log('Found files from list endpoint:', files);
        
        discovered.iotDeviceReport = files.find(f => f.startsWith('iot-device-report-') && f.endsWith('.csv')) || null;
        discovered.consumptionReport = files.find(f => f.startsWith('consumption-report-') && f.endsWith('.csv')) || null;
        discovered.assetModelList = files.find(f => f.startsWith('Asset_Model_List_') && f.endsWith('.csv')) || null;
        
        if (discovered.iotDeviceReport || discovered.consumptionReport || discovered.assetModelList) {
          console.log('Successfully discovered files from list endpoint');
          return discovered;
        }
      }
    } catch (listError) {
      console.log('List endpoint not available...');
    }

    // Method 3: Try to fetch a simple text file listing (files.txt)
    try {
      const filesListResponse = await fetch('/IOTReportData/files.txt', {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (filesListResponse.ok) {
        const text = await filesListResponse.text();
        const files = text.split('\n')
          .map(line => line.trim())
          .filter(line => line && line.endsWith('.csv'));
        
        console.log('Found files from files.txt:', files);
        
        discovered.iotDeviceReport = files.find(f => f.startsWith('iot-device-report-') && f.endsWith('.csv')) || null;
        discovered.consumptionReport = files.find(f => f.startsWith('consumption-report-') && f.endsWith('.csv')) || null;
        discovered.assetModelList = files.find(f => f.startsWith('Asset_Model_List_') && f.endsWith('.csv')) || null;
        
        if (discovered.iotDeviceReport || discovered.consumptionReport || discovered.assetModelList) {
          console.log('Successfully discovered files from files.txt');
          return discovered;
        }
      }
    } catch (filesListError) {
      console.log('files.txt not available...');
    }

    // Method 4: Try to fetch files by testing HEAD requests with common patterns
    // This is a fallback - try recent dates/timestamps
    console.log('Trying to discover files by testing common patterns (this may take a moment)...');
    
    // Try recent dates for iot-device-report (format: YYYY-MM-DD_HH-MM-SS)
    const today = new Date();
    const datesToTry = [];
    for (let i = 0; i < 60; i++) { // Try last 60 days
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().slice(0, 10);
      datesToTry.push(dateStr);
    }
    
    // Test iot-device-report files - try common time patterns and also try to find any file with the prefix
    // First, try to find by testing HEAD requests for recent dates with common times
    const timePatterns = ['12-01-53', '00-00-00', '10-00-00', '14-00-00', '08-00-00', '16-00-00', '20-26-10', '12-00-00'];
    for (const dateStr of datesToTry.slice(0, 10)) { // Try last 10 days
      for (const time of timePatterns) {
        const testFile = `iot-device-report-${dateStr}_${time}.csv`;
        try {
          const test = await fetch(`/IOTReportData/${testFile}`, { method: 'HEAD', cache: 'no-cache' });
          if (test.ok) {
            discovered.iotDeviceReport = testFile;
            console.log(`✓ Found iot-device-report: ${testFile}`);
            break;
          }
        } catch (e) {
          // Continue trying
        }
      }
      if (discovered.iotDeviceReport) break;
    }
    
    // If still not found, try a more aggressive approach: test multiple time combinations for recent dates
    if (!discovered.iotDeviceReport) {
      console.log('Trying extended time pattern search for iot-device-report...');
      for (const dateStr of datesToTry.slice(0, 3)) { // Last 3 days
        for (let hour = 0; hour < 24; hour++) {
          for (let min = 0; min < 60; min += 5) { // Every 5 minutes
            const time = `${hour.toString().padStart(2, '0')}-${min.toString().padStart(2, '0')}-00`;
            const testFile = `iot-device-report-${dateStr}_${time}.csv`;
            try {
              const test = await fetch(`/IOTReportData/${testFile}`, { method: 'HEAD', cache: 'no-cache' });
              if (test.ok) {
                discovered.iotDeviceReport = testFile;
                console.log(`✓ Found iot-device-report: ${testFile}`);
                break;
              }
            } catch (e) {
              // Continue trying
            }
          }
          if (discovered.iotDeviceReport) break;
        }
        if (discovered.iotDeviceReport) break;
      }
    }
    
    // Test consumption-report files (format: consumption-report-XXXXXXXXXXXX.csv)
    // Try timestamp patterns - check last 7 days worth of timestamps
    const now = Date.now();
    const timestampsToTry = [];
    for (let i = 0; i < 7; i++) {
      const ts = (now - (i * 86400000)).toString();
      timestampsToTry.push(ts.slice(0, 13)); // 13 digits
      timestampsToTry.push(ts.slice(0, 12)); // 12 digits
    }
    
    for (const ts of timestampsToTry.slice(0, 10)) {
      const testFile = `consumption-report-${ts}.csv`;
      try {
        const test = await fetch(`/IOTReportData/${testFile}`, { method: 'HEAD', cache: 'no-cache' });
        if (test.ok) {
          discovered.consumptionReport = testFile;
          console.log(`✓ Found consumption-report: ${testFile}`);
          break;
        }
      } catch (e) {
        // Continue trying
      }
    }
    
    // Test Asset_Model_List files (format: Asset_Model_List_XXXXXXXXXXXX.csv)
    for (const ts of timestampsToTry.slice(0, 10)) {
      const testFile = `Asset_Model_List_${ts}.csv`;
      try {
        const test = await fetch(`/IOTReportData/${testFile}`, { method: 'HEAD', cache: 'no-cache' });
        if (test.ok) {
          discovered.assetModelList = testFile;
          console.log(`✓ Found Asset_Model_List: ${testFile}`);
          break;
        }
      } catch (e) {
        // Continue trying
      }
    }
    
    return discovered;
  };

  // Discover and load file names from IOTReportData folder
  useEffect(() => {
    discoverFiles();
  }, []);

  // Debug: Log file names when they change
  useEffect(() => {
    console.log('File names state updated:', fileNames);
  }, [fileNames]);

  // Load standard consumption rates from API
  useEffect(() => {
    console.log('Loading standard consumption rates from API...');
    loadStandardRates();
  }, []); // Load once on component mount

  // Load full tank refueling data and fuel added data
  useEffect(() => {
    if (fileNames.consumptionReport) {
      console.log('Loading full tank data with file:', fileNames.consumptionReport);
    loadFullTankData();
    loadFuelAddedData();
    } else {
      console.log('Waiting for consumptionReport file name...');
    }
  }, [fileNames.consumptionReport]);

  // Removed auto-loading useEffect - data will only load when Reload button is clicked
  
  // Load asset types from API and build ID to name map
  const loadAssetTypeMap = async () => {
    try {
      console.log('Fetching asset types to build asset type map...');
      const result = await apiGet('/asset-type', {
        headers: {
          'Accept': '*/*',
        },
      });

      if (!result.success) {
        console.warn('Failed to fetch asset types:', result.error);
        return {};
      }

      const assetTypes = result.data || [];
      const typeMap = {};
      
      assetTypes.forEach(assetType => {
        if (assetType._id) {
          // Map ID to name
          typeMap[assetType._id] = assetType.name || '';
        }
      });
      
      console.log(`Built asset type map with ${Object.keys(typeMap).length} asset types`);
      setAssetTypeMap(typeMap);
      return typeMap;
    } catch (error) {
      console.error('Error loading asset type map:', error);
      return {};
    }
  };

  // Fetch asset list and build fuel bowser map
  const loadFuelBowserMap = async () => {
    try {
      console.log('Fetching asset list to build fuel bowser map...');
      const result = await apiGet('/asset/asset-all', {
        headers: {
          'Accept': '*/*',
        },
      });

      if (!result.success) {
        console.warn('Failed to fetch asset list for fuel bowser filtering:', result.error);
        return {};
      }

      const assets = result.data || [];
      const bowserMap = {};
      
      assets.forEach(asset => {
        if (asset.assetCode) {
          // Check if asset has equipmentSubType with assetType that has isBowser
          // Primary path: asset.equipmentSubType.assetType.isBowser
          let isBowser = false;
          
          // Check equipmentSubType.assetType.isBowser (primary path based on API structure)
          if (asset.equipmentSubType && typeof asset.equipmentSubType === 'object' && asset.equipmentSubType !== null) {
            const assetType = asset.equipmentSubType.assetType;
            if (assetType && typeof assetType === 'object' && assetType !== null) {
              isBowser = assetType.isBowser === true || assetType.isBowser === 'true';
            }
          }
          
          // Fallback: Check asset.assetType directly (for backward compatibility)
          if (!isBowser && asset.assetType) {
            if (typeof asset.assetType === 'object' && asset.assetType !== null) {
              isBowser = asset.assetType.isBowser === true || 
                        asset.assetType.isBowser === 'true' ||
                        asset.assetType.isFuelBowser === true ||
                        asset.assetType.isAFuelBowser === true ||
                        asset.assetType['Is a fuel Bowser'] === true ||
                        false;
            }
          }
          
          if (isBowser) {
            bowserMap[asset.assetCode] = true;
            console.log(`Found bowser asset (isBowser=true): ${asset.assetCode}`);
          }
        }
      });
      
      console.log(`Built fuel bowser map with ${Object.keys(bowserMap).length} fuel bowser assets`);
      setFuelBowserMap(bowserMap);
      return bowserMap;
    } catch (error) {
      console.error('Error loading fuel bowser map:', error);
      return {};
    }
  };

  useEffect(() => {
    applyFilters();
  }, [summaryData, selectedAssetType, selectedAssetSubType, selectedAssetModel, selectedAssetCode, selectedLocation, selectedSubLocation]);

  // Update filter options when selections change
  useEffect(() => {
    if (summaryData.length > 0) {
      let filtered = summaryData;
      
      if (selectedAssetType) {
        filtered = filtered.filter(item => item.assetType === selectedAssetType);
      }
      
      const subTypes = [...new Set(filtered.map(item => item.assetSubType).filter(Boolean))].sort();
      setAssetSubTypes(subTypes);
      
      if (selectedAssetSubType) {
        filtered = filtered.filter(item => item.assetSubType === selectedAssetSubType);
      }
      
      const models = [...new Set(filtered.map(item => item.assetModel).filter(Boolean))].sort();
      setAssetModels(models);
      
      if (selectedLocation) {
        filtered = filtered.filter(item => item.location === selectedLocation);
      }
      
      const subLocs = [...new Set(filtered.map(item => item.subLocation).filter(Boolean))].sort();
      setSubLocations(subLocs);
    }
  }, [summaryData, selectedAssetType, selectedAssetSubType, selectedLocation]);

  const loadFullTankData = async () => {
    if (!fileNames.consumptionReport) {
      console.error('Consumption report file name not discovered yet');
      setFullTankDataLoaded(true); // Mark as loaded to prevent blocking
      return;
    }
    
    try {
      console.log(`Attempting to load consumption report: /IOTReportData/${fileNames.consumptionReport}`);
      // Load Consumption Report CSV - it uses comma delimiter
      const response = await fetch(`/IOTReportData/${fileNames.consumptionReport}`, {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch consumption report: ${response.status} ${response.statusText}`);
      }
      
      const text = await response.text();
      console.log(`Successfully loaded consumption report, size: ${text.length} characters`);
      
      return new Promise((resolve) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          delimiter: ',', // Comma delimiter
          quoteChar: '"', // Handle quoted values
          complete: (results) => {
            const fullTankMap = {};
            
            let processedCount = 0;
            let fullTankCount = 0;
            
            // Debug: Check first row structure
            if (results.data.length > 0) {
              console.log('First row keys from Papa.parse:', Object.keys(results.data[0]));
              console.log('First row sample:', {
                'Date': results.data[0]['Date'],
                'Asset': results.data[0]['Asset'],
                'Full Tank': results.data[0]['Full Tank']
              });
            }
            
            results.data.forEach(row => {
              processedCount++;
              // Papa.parse should automatically strip quotes from both headers and values
              const fullTank = (row['Full Tank'] || '').trim().toLowerCase();
              
              // Debug first few rows
              if (processedCount <= 3) {
                console.log(`Row ${processedCount} - Full Tank field:`, {
                  raw: row['Full Tank'],
                  trimmed: fullTank,
                  isTrue: fullTank === 'true' || fullTank === '1'
                });
              }
              
              // Only process full tank events
              if (fullTank === 'true' || fullTank === '1') {
                fullTankCount++;
                const assetCode = (row['Asset'] || '').trim();
                const dateStr = (row['Date'] || '').trim();
                
                if (assetCode && dateStr) {
                  // Parse date (format: DD-MM-YYYY_HH:MM:SS or DD-MM-YYYY)
                  // Remove any quotes that might be around the date string
                  const cleanDateStr = dateStr.replace(/^["']|["']$/g, '');
                  const datePart = cleanDateStr.split('_')[0]; // Get date part before underscore
                  const date = moment(datePart, 'DD-MM-YYYY', true);
                  
                  // Debug first few date parses
                  if (fullTankCount <= 3) {
                    console.log(`Date parsing - Original: '${dateStr}', Cleaned: '${cleanDateStr}', DatePart: '${datePart}', Valid: ${date.isValid()}, MonthKey: ${date.isValid() ? date.format('YYYY-MM') : 'N/A'}`);
                  }
                  
                  if (date.isValid()) {
                    const monthKey = date.format('YYYY-MM');
                    const assetMonthKey = `${assetCode}_${monthKey}`;
                    
                    if (!fullTankMap[assetMonthKey]) {
                      fullTankMap[assetMonthKey] = {
                        assetCode,
                        month: monthKey,
                        events: [],
                        consumptionRates: []
                      };
                    }
                    
                    // Extract consumption rates based on unit
                    // Priority: KMS/ Qty (KMS/Ltr), Qty/ KMS (Ltr/KMS), Qty/ HOURS (Ltr/Hrs), HOURS/ Qty (Hrs/Ltr)
                    const kmsPerQty = getNumericValue(row, 'KMS/ Qty');
                    const qtyPerKms = getNumericValue(row, 'Qty/ KMS');
                    const hoursPerQty = getNumericValue(row, 'HOURS/ Qty');
                    const qtyPerHours = getNumericValue(row, 'Qty/ HOURS');
                    
                    // Determine which rate to use (prefer KMS/Ltr or Ltr/Hrs format)
                    let consumptionRate = null;
                    let consumptionRateUnit = null;
                    
                    // KMS/ Qty = KMS per Liter (KMS/Ltr) - preferred for vehicles
                    if (kmsPerQty > 0) {
                      consumptionRate = kmsPerQty;
                      consumptionRateUnit = 'KMS/Ltr';
                    } 
                    // Qty/ HOURS = Liters per Hour (Ltr/Hrs) - preferred for equipment
                    else if (qtyPerHours > 0) {
                      consumptionRate = qtyPerHours;
                      consumptionRateUnit = 'Ltr/Hrs';
                    }
                    // Qty/ KMS = Liters per KMS (Ltr/KMS) - alternative for vehicles
                    else if (qtyPerKms > 0) {
                      consumptionRate = qtyPerKms;
                      consumptionRateUnit = 'Ltr/KMS';
                    }
                    // HOURS/ Qty = Hours per Liter (Hrs/Ltr) - alternative for equipment
                    else if (hoursPerQty > 0) {
                      consumptionRate = hoursPerQty;
                      consumptionRateUnit = 'Hrs/Ltr';
                    }
                    
                    if (consumptionRate !== null) {
                      fullTankMap[assetMonthKey].events.push({
                        date: date.format('DD-MM-YYYY'),
                        consumptionRate,
                        consumptionRateUnit
                      });
                      fullTankMap[assetMonthKey].consumptionRates.push(consumptionRate);
                    }
                  }
                }
              }
            });
            
            console.log(`Processed ${processedCount} rows, found ${fullTankCount} full tank events`);
            console.log(`Loaded full tank data for ${Object.keys(fullTankMap).length} asset-month combinations`);
            // Debug: Log first few entries
            const sampleKeys = Object.keys(fullTankMap).slice(0, 5);
            sampleKeys.forEach(key => {
              console.log(`Full Tank Key: ${key}, Events: ${fullTankMap[key].events.length}`);
            });
            
            // Store raw CSV data for breakdown view
            setFullTankRawData(results.data);
            setFullTankDataMap(fullTankMap);
            setFullTankDataLoaded(true);
            resolve(fullTankMap);
          },
          error: (error) => {
            console.error('Error loading full tank data:', error);
            setFullTankDataMap({});
            setFullTankDataLoaded(true); // Mark as loaded even on error
            resolve({});
          }
        });
      });
    } catch (error) {
      console.error('Error loading full tank data:', error);
      setFullTankDataMap({});
      setFullTankDataLoaded(true); // Mark as loaded even on error
    }
  };

  const loadFullTankDataFromApi = async (assetCodes, startTimestamp, endTimestamp, selectedMonth) => {
    try {
      console.log(`Loading full tank data from API for ${assetCodes.length} assets`);
      const fullTankMap = {};
      const monthKey = selectedMonth.format('YYYY-MM');
      const allApiEvents = []; // Store all API events for breakdown view
      
      // Fetch full tank events for each asset
      for (const assetCode of assetCodes) {
        try {
          const result = await fetchFullTankEventsForPeriod(assetCode, startTimestamp, endTimestamp);
          
          if (result.success && result.events.length > 0) {
            const assetMonthKey = `${assetCode}_${monthKey}`;
            
            // Filter events to only include those in the selected month
            const monthEvents = result.events.filter(event => {
              const eventDate = moment(event.date);
              return eventDate.format('YYYY-MM') === monthKey;
            });
            
            if (monthEvents.length > 0) {
              // Fetch all fuel entries for this asset to calculate quantity consumed and include in breakdown
              // We need to get the asset ID first
              let allFuelEntries = [];
              try {
                const assetResult = await findAssetIdByCode(assetCode);
                if (assetResult.success) {
                  const fuelEntriesResult = await fetchFuelEntries(
                    assetResult.assetId,
                    moment(startTimestamp).toISOString(),
                    moment(endTimestamp).toISOString()
                  );
                  if (fuelEntriesResult.success) {
                    allFuelEntries = fuelEntriesResult.data || [];
                  }
                }
              } catch (error) {
                console.warn(`Could not fetch fuel entries for ${assetCode}:`, error);
              }
              
              // Sort all fuel entries by date (keep all entries, not just month entries)
              // The API fetches previous full tank events to enable calculation for first event in range
              const sortedAllFuelEntries = [...allFuelEntries].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
              
              // Filter all fuel entries to only include those in the selected month (for display)
              const monthFuelEntries = sortedAllFuelEntries.filter(entry => {
                const entryDate = moment(entry.date);
                return entryDate.format('YYYY-MM') === monthKey;
              });
              
              // Get standard consumption rate unit from asset model (if available)
              let standardRateUnit = null;
              if (monthEvents.length > 0 && monthEvents[0].assetModel) {
                const assetModel = monthEvents[0].assetModel;
                if (assetModel.standardFuelConsumptionRate && Array.isArray(assetModel.standardFuelConsumptionRate) && assetModel.standardFuelConsumptionRate.length > 0) {
                  const firstRate = assetModel.standardFuelConsumptionRate[0];
                  if (firstRate.perType && firstRate.perType.name) {
                    const unitName = firstRate.perType.name.toUpperCase();
                    if (unitName.includes('KM')) {
                      standardRateUnit = 'KM';
                    } else if (unitName.includes('HOUR')) {
                      standardRateUnit = 'HOURS';
                    } else if (unitName.includes('KWH')) {
                      standardRateUnit = 'KWH';
                    }
                  }
                }
              }
              
              // Transform ALL fuel entries (both full tank and non-full tank) to CSV-like format for breakdown view
              const transformedEvents = monthFuelEntries.map((event, entryIndex) => {
                const isFullTank = event.isFullTank === true;
                
                // Find the index of this event in the full tank events array (if it's a full tank event)
                const fullTankIndex = isFullTank ? monthEvents.findIndex(e => 
                  (e._id === event._id) || (e.voucherNo === event.voucherNo && event.voucherNo)
                ) : -1;
                const eventDate = moment(event.date);
                const dateStr = eventDate.format('DD-MM-YYYY');
                const timeStr = eventDate.format('HH:mm:ss');
                const dateTimeStr = `${dateStr}_${timeStr}`;
                
                // Extract all meter readings (KMS, HOURS, KWH)
                let kmsReading = null;
                let hoursReading = null;
                let kwhReading = null;
                
                if (event.mileage && Array.isArray(event.mileage)) {
                  event.mileage.forEach(meter => {
                    const meterName = (meter.name || '').toUpperCase();
                    if (meterName.includes('KM') || meterName.includes('ODO') || meterName.includes('DISTANCE')) {
                      kmsReading = meter.reading;
                    } else if (meterName.includes('HOUR') || meterName.includes('HRS')) {
                      hoursReading = meter.reading;
                    } else if (meterName.includes('KWH')) {
                      kwhReading = meter.reading;
                    }
                  });
                }
                
                // Calculate quantity consumed and meter run ONLY for full tank events
                // Full Tank to Full Tank Logic:
                // - Fuel Consumed = Current full tank event quantity + all non-full tank events between previous and current
                // - Fuel added in previous full tank event is NOT included
                // - Meter Run = Current meter reading - Previous meter reading
                let quantityConsumed = null;
                let kmsConsumed = null;
                let hoursConsumed = null;
                let kwhConsumed = null;
                
                // Only calculate consumption data for full tank events
                // Find previous full tank event from ALL fetched entries (not just month entries)
                // This allows calculation for the first full tank event in the month
                if (isFullTank) {
                  const currentEventDate = moment(event.date);
                  
                  // Find the previous full tank event from ALL fetched entries (including those outside the month)
                  let previousEvent = null;
                  for (let i = sortedAllFuelEntries.length - 1; i >= 0; i--) {
                    const entry = sortedAllFuelEntries[i];
                    if (entry.isFullTank === true) {
                      const entryDate = moment(entry.date);
                      if (entryDate.isBefore(currentEventDate)) {
                        previousEvent = entry;
                        break;
                      }
                    }
                  }
                  
                  if (previousEvent) {
                    const previousEventDate = moment(previousEvent.date);
                    
                    // Fuel Consumed = Current full tank event quantity + all non-full tank events between previous and current
                    // Note: Previous full tank event quantity is NOT included
                    // Use ALL fetched entries (not just month entries) to include entries between previous and current
                    quantityConsumed = sortedAllFuelEntries
                      .filter(entry => {
                        const entryDate = moment(entry.date);
                        // Include current full tank event and all entries after previous event but before current event
                        return (entryDate.isSame(currentEventDate) || 
                                (entryDate.isAfter(previousEventDate) && entryDate.isBefore(currentEventDate)));
                      })
                      .reduce((sum, entry) => sum + (entry.quantity || 0), 0);
                    
                    // Calculate meter run: Current meter reading - Previous meter reading
                    // Use calculatedReading for run calculation (cumulative, handles meter resets)
                    if (event.mileage && Array.isArray(event.mileage) && 
                        previousEvent.mileage && Array.isArray(previousEvent.mileage)) {
                      // Find corresponding meters
                      event.mileage.forEach(currentMeter => {
                        const meterName = (currentMeter.name || '').toUpperCase();
                        const previousMeter = previousEvent.mileage.find(m => 
                          (m.name || '').toUpperCase() === meterName
                        );
                        
                        // Use calculatedReading for accurate run calculation
                        const currentReading = getCalculatedReading(currentMeter);
                        const previousReading = getCalculatedReading(previousMeter);
                        
                        if (currentReading !== null && previousReading !== null) {
                          const consumed = currentReading - previousReading;
                          
                          if (meterName.includes('KM') || meterName.includes('ODO') || meterName.includes('DISTANCE')) {
                            kmsConsumed = consumed > 0 ? consumed : null;
                          } else if (meterName.includes('HOUR') || meterName.includes('HRS')) {
                            hoursConsumed = consumed > 0 ? consumed : null;
                          } else if (meterName.includes('KWH')) {
                            kwhConsumed = consumed > 0 ? consumed : null;
                          }
                        }
                      });
                    }
                  }
                }
                
                // Calculate consumption rate based on standard rate unit or available data
                // Only calculate for full tank events (non-full tank events don't have consumption rates)
                let kmsPerQty = null;
                let qtyPerKms = null;
                let hoursPerQty = null;
                let qtyPerHours = null;
                
                if (isFullTank) {
                  // Get consumption rate from API result
                  // Note: consumptionRates array has rates for events starting from index 1 (second event)
                  // consumptionRates[0] = rate for event at index 1, consumptionRates[1] = rate for event at index 2, etc.
                  let consumptionRate = null;
                  let consumptionRateUnit = result.avgConsumptionRateUnit;
                  
                  if (fullTankIndex > 0 && result.consumptionRates && result.consumptionRates.length >= fullTankIndex) {
                    // Event at fullTankIndex i has rate at consumptionRates[i-1]
                    consumptionRate = result.consumptionRates[fullTankIndex - 1];
                  } else if (result.avgConsumptionRate && fullTankIndex > 0) {
                    // Fallback to average rate if specific rate not available
                    consumptionRate = result.avgConsumptionRate;
                  }
                  
                  // Calculate consumption rate based on standard rate unit or available consumed values
                  if (standardRateUnit === 'KM' && quantityConsumed > 0 && kmsConsumed > 0) {
                    // Standard is KM-based, calculate KMS/Ltr
                    kmsPerQty = kmsConsumed / quantityConsumed;
                  } else if (standardRateUnit === 'HOURS' && quantityConsumed > 0 && hoursConsumed > 0) {
                    // Standard is HOURS-based, calculate Ltr/Hrs
                    qtyPerHours = quantityConsumed / hoursConsumed;
                  } else if (standardRateUnit === 'KWH' && quantityConsumed > 0 && kwhConsumed > 0) {
                    // Standard is KWH-based, calculate Ltr/KWH (or KWH/Ltr depending on standard format)
                    // For now, use Ltr/KWH format
                    const qtyPerKwh = quantityConsumed / kwhConsumed;
                    // Store in a field that can be displayed (might need to add KWH/ Qty or Qty/ KWH column)
                  } else if (consumptionRate && consumptionRateUnit) {
                    // Fall back to API calculated rate
                    if (consumptionRateUnit === 'KM/L') {
                      // KM/L = KMS per Liter → 'KMS/ Qty'
                      kmsPerQty = consumptionRate;
                    } else if (consumptionRateUnit === 'L/KM') {
                      // L/KM = Liters per KMS → 'Qty/ KMS'
                      qtyPerKms = consumptionRate;
                    } else if (consumptionRateUnit === 'L/HOURS') {
                      // L/HOURS = Liters per HOURS → 'Qty/ HOURS'
                      qtyPerHours = consumptionRate;
                    } else if (consumptionRateUnit === 'HOURS/L') {
                      // HOURS/L = HOURS per Liter → 'HOURS/ Qty'
                      hoursPerQty = consumptionRate;
                    }
                  } else if (quantityConsumed > 0) {
                    // Calculate from available consumed values (fallback)
                    if (kmsConsumed > 0) {
                      kmsPerQty = kmsConsumed / quantityConsumed;
                    } else if (hoursConsumed > 0) {
                      qtyPerHours = quantityConsumed / hoursConsumed;
                    } else if (kwhConsumed > 0) {
                      // KWH-based calculation
                      const qtyPerKwh = quantityConsumed / kwhConsumed;
                    }
                  }
                }
                
                // Transform to CSV-like format
                return {
                  'Date': dateTimeStr,
                  'Asset': assetCode,
                  'Full Tank': isFullTank ? 'true' : 'false',
                  'Voucher': event.voucherNo || '',
                  'Quantity Refilled': event.quantity || 0,
                  'Quantity Consumed': quantityConsumed,
                  'KMS Reading': kmsReading,
                  'HOURS Reading': hoursReading,
                  'KWH Reading': kwhReading,
                  'KMS Consumed': kmsConsumed,
                  'HOURS Consumed': hoursConsumed,
                  'KWH Consumed': kwhConsumed,
                  'KMS/ Qty': kmsPerQty,
                  'Qty/ KMS': qtyPerKms,
                  'HOURS/ Qty': hoursPerQty,
                  'Qty/ HOURS': qtyPerHours,
                  // Store standard rate unit for column visibility logic
                  _standardRateUnit: standardRateUnit,
                  // Store original API event for reference
                  _apiEvent: event
                };
              });
              
              // Add transformed events to the collection
              allApiEvents.push(...transformedEvents);
              
              fullTankMap[assetMonthKey] = {
                assetCode,
                month: monthKey,
                events: monthEvents.map(event => ({
                  date: moment(event.date).format('DD-MM-YYYY'),
                  consumptionRate: null, // Will be calculated from consumptionRates array
                  consumptionRateUnit: result.avgConsumptionRateUnit
                })),
                consumptionRates: result.consumptionRates || []
              };
              
              // If we have consumption rates, update events with individual rates
              if (result.consumptionRates && result.consumptionRates.length > 0) {
                // Map rates to events (each rate is between consecutive events)
                // For now, assign average rate to all events
                const avgRate = result.avgConsumptionRate;
                fullTankMap[assetMonthKey].events = fullTankMap[assetMonthKey].events.map(event => ({
                  ...event,
                  consumptionRate: avgRate
                }));
              }
              
              console.log(`Loaded ${monthEvents.length} full tank events for ${assetCode} in ${monthKey}`);
            }
          }
        } catch (error) {
          console.error(`Error loading full tank data for ${assetCode}:`, error);
          // Continue with other assets
        }
      }
      
      console.log(`Loaded full tank data from API for ${Object.keys(fullTankMap).length} asset-month combinations`);
      console.log(`Total API events for breakdown: ${allApiEvents.length}`);
      
      // Store transformed API events for breakdown view
      setFullTankRawData(allApiEvents);
      setFullTankDataMap(fullTankMap);
      // Don't set fullTankDataLoaded here - it will be set after re-aggregation completes in the caller
      return fullTankMap;
    } catch (error) {
      console.error('Error loading full tank data from API:', error);
      setFullTankDataMap({});
      // Don't set fullTankDataLoaded here - let the caller handle it after attempting re-aggregation
      return {};
    }
  };

  const loadFuelAddedData = async () => {
    if (!fileNames.consumptionReport) {
      console.error('Consumption report file name not discovered yet');
      setFuelAddedDataLoaded(true); // Mark as loaded to prevent blocking
      return;
    }
    
    try {
      console.log(`Attempting to load fuel added data from: /IOTReportData/${fileNames.consumptionReport}`);
      // Load Consumption Report CSV - it uses comma delimiter
      const response = await fetch(`/IOTReportData/${fileNames.consumptionReport}`, {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch consumption report: ${response.status} ${response.statusText}`);
      }
      
      const text = await response.text();
      console.log(`Successfully loaded fuel added data, size: ${text.length} characters`);
      
      return new Promise((resolve) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          delimiter: ',', // Comma delimiter
          quoteChar: '"', // Handle quoted values
          complete: (results) => {
            const fuelAddedMap = {};
            
            results.data.forEach(row => {
              const assetCode = (row['Asset'] || '').trim();
              const dateStr = (row['Date'] || '').trim();
              const quantityRefilled = getNumericValue(row, 'Quantity Refilled');
              
              if (assetCode && dateStr && quantityRefilled > 0) {
                // Parse date (format: DD-MM-YYYY_HH:MM:SS or DD-MM-YYYY)
                const cleanDateStr = dateStr.replace(/^["']|["']$/g, '');
                const datePart = cleanDateStr.split('_')[0]; // Get date part before underscore
                const date = moment(datePart, 'DD-MM-YYYY', true);
                
                if (date.isValid()) {
                  const monthKey = date.format('YYYY-MM');
                  const assetMonthKey = `${assetCode}_${monthKey}`;
                  
                  if (!fuelAddedMap[assetMonthKey]) {
                    fuelAddedMap[assetMonthKey] = {
                      assetCode,
                      month: monthKey,
                      totalFuelAdded: 0
                    };
                  }
                  
                  // Sum up all fuel added (both full tank and non-full tank events)
                  fuelAddedMap[assetMonthKey].totalFuelAdded += quantityRefilled;
                }
              }
            });
            
            console.log(`Loaded fuel added data for ${Object.keys(fuelAddedMap).length} asset-month combinations`);
            setFuelAddedMap(fuelAddedMap);
            setFuelAddedDataLoaded(true);
            resolve(fuelAddedMap);
          },
          error: (error) => {
            console.error('Error loading fuel added data:', error);
            setFuelAddedMap({});
            setFuelAddedDataLoaded(true); // Mark as loaded even on error
            resolve({});
          }
        });
      });
    } catch (error) {
      console.error('Error loading fuel added data:', error);
      setFuelAddedMap({});
      setFuelAddedDataLoaded(true); // Mark as loaded even on error
    }
  };

  const loadStandardRates = async () => {
    try {
      console.log('Loading standard consumption rates from API...');
      
      // Fetch all asset models from API (same approach as HDI Consumption Comparison and Fuel Alert Analysis)
      const modelsResult = await apiGet('/asset-model', {
        headers: {
          'Accept': '*/*',
        },
      });

      if (!modelsResult.success) {
        console.error(`Failed to load asset models: ${modelsResult.error}`);
        setStandardRatesMap({});
        return {};
      }

      // Handle different response structures
      let modelsData = [];
      if (Array.isArray(modelsResult.data)) {
        modelsData = modelsResult.data;
      } else if (modelsResult.data && Array.isArray(modelsResult.data.data)) {
        modelsData = modelsResult.data.data;
      } else if (modelsResult.data && Array.isArray(modelsResult.data.models)) {
        modelsData = modelsResult.data.models;
      } else if (modelsResult.data && typeof modelsResult.data === 'object') {
        // Try to find an array property
        for (const key of Object.keys(modelsResult.data)) {
          if (Array.isArray(modelsResult.data[key])) {
            modelsData = modelsResult.data[key];
            break;
          }
        }
      }
      
      if (!Array.isArray(modelsData)) {
        console.error('Could not extract array from response. Full response:', modelsResult.data);
        setStandardRatesMap({});
        return {};
      }
      
      console.log(`Extracted ${modelsData.length} models from API response`);

      // Create rates map keyed by model name (normalized: trimmed, lowercase for matching)
      const ratesMap = {};
      const ratesMapByNormalizedName = {}; // For case-insensitive matching
      
      modelsData.forEach(model => {
        const modelName = (model.name || '').trim();
        if (!modelName) return;
        
        // Normalize name for matching (lowercase, trimmed)
        const normalizedName = modelName.toLowerCase().trim();
        
        // Extract standard consumption rates from array structure
        if (model.standardFuelConsumptionRate && Array.isArray(model.standardFuelConsumptionRate) && model.standardFuelConsumptionRate.length > 0) {
          let kmsPerLtr = null;
          let ltrPerHour = null;
          
          model.standardFuelConsumptionRate.forEach(rateObj => {
            const fuelQty = rateObj.fuelQty;
            
            if (fuelQty === null || fuelQty === undefined || isNaN(fuelQty) || fuelQty <= 0) {
              return; // Skip invalid rates
            }
            
            // Extract unit from perType.name
            if (rateObj.perType && rateObj.perType.name) {
              const unitName = rateObj.perType.name.toUpperCase();
              
              // API format is "L/KM" or "L/HOURS" (litres per unit)
              // Code expects "KMS/L" (km per litre) or "L/HOURS" (litres per hour)
              if (unitName === 'KMS' || unitName === 'KM' || unitName.includes('KM')) {
                // Convert L/KM to KMS/L: if API says 0.1 L/KM, that means 10 KMS/L
                kmsPerLtr = 1 / fuelQty;
              } else if (unitName === 'HOURS' || unitName === 'HOUR' || unitName === 'HRS' || unitName.includes('HOUR')) {
                // L/HOURS stays the same
                ltrPerHour = fuelQty;
              } else if (unitName === 'KWH' || unitName.includes('KWH')) {
                // For KWH, we might want to handle it differently, but for now skip
                // Could add support later if needed
              }
            }
          });
          
          // Only add to map if we have at least one valid rate
          if (kmsPerLtr !== null || ltrPerHour !== null) {
            ratesMap[modelName] = {
              kmsPerLtr,
              ltrPerHour
            };
            // Also store by normalized name for case-insensitive lookup
            ratesMapByNormalizedName[normalizedName] = {
              kmsPerLtr,
              ltrPerHour,
              originalName: modelName // Keep original name for reference
            };
          }
        }
      });
      
      // Store both exact match and normalized match maps
      // Store normalized map as the primary one for case-insensitive matching
      const finalRatesMap = {};
      Object.keys(ratesMapByNormalizedName).forEach(normalizedKey => {
        const rateData = ratesMapByNormalizedName[normalizedKey];
        // Store with both normalized key and original name for flexible lookup
        finalRatesMap[normalizedKey] = rateData;
        if (rateData.originalName) {
          finalRatesMap[rateData.originalName] = {
            kmsPerLtr: rateData.kmsPerLtr,
            ltrPerHour: rateData.ltrPerHour
          };
        }
      });
      
      console.log(`Loaded ${Object.keys(finalRatesMap).length} standard consumption rates from API`);
      console.log('Sample rates:', Object.keys(finalRatesMap).slice(0, 5));
      setStandardRatesMap(finalRatesMap);
      return finalRatesMap;
    } catch (error) {
      console.error('Error loading standard rates from API:', error);
      setStandardRatesMap({});
      return {};
    }
  };

  const loadComparisonData = async () => {
    if (!selectedPeriods || selectedPeriods.length === 0) {
      message.warning('Please select at least one period to compare');
      return;
    }
    
    setLoading(true);
    try {
      // Use API - fetch data for the date range covering all selected periods
      const allPeriodsStart = moment.min(selectedPeriods.map(p => p.clone().startOf('month')));
      const allPeriodsEnd = moment.max(selectedPeriods.map(p => p.clone().endOf('month')));
      
      const startTimestamp = allPeriodsStart.startOf('day').valueOf();
      const endTimestamp = allPeriodsEnd.endOf('day').valueOf();
      
      console.log('Fetching comparison data from API for periods:', {
        periods: selectedPeriods.map(p => p.format('YYYY-MM')),
        dateRange: `${allPeriodsStart.format('YYYY-MM-DD')} to ${allPeriodsEnd.format('YYYY-MM-DD')}`,
        startTimestamp,
        endTimestamp
      });
      
      const result = await fetchDailyFuelDataReport(startTimestamp, endTimestamp, {
        assets: null,
        locations: ["all"],
        subLocations: ["all"],
        assetTypes: [],
        assetSubTypes: [],
        assetModels: [],
        frequency: "Daily"
      });
      
      if (!result.success) {
        message.error(`Failed to fetch data from API: ${result.error}`);
        setLoading(false);
        return;
      }
      
      console.log(`Successfully fetched ${result.data.length} daily records from API for comparison`);
      
      // Load fuel bowser map and asset type map
      const bowserMap = await loadFuelBowserMap();
      const typeMap = await loadAssetTypeMap();
      
      // Transform API response to CSV format (with fuel bowser filtering and asset type mapping)
      const allCsvData = transformApiResponseToCsvFormat(result.data, bowserMap, typeMap);
      console.log(`Transformed to ${allCsvData.length} CSV-like rows for comparison (after filtering fuel bowser assets)`);
      
      // For API mode, set fuelAddedDataLoaded to true (we don't use CSV consumption report)
      setFuelAddedDataLoaded(true);
      
      // Store all daily data for breakdown view
      setDailyData(allCsvData);
      
      // Aggregate data for each period immediately (without waiting for full tank data)
      const allPeriodData = [];
      for (const period of selectedPeriods) {
        const monthStart = moment(period).startOf('month');
        const monthEnd = moment(period).endOf('month');
        
        const filtered = allCsvData.filter(row => {
          const dateStr = row['Date'] || row['"Date"'] || row['date'] || row['DATE'] || '';
          if (!dateStr) return false;
          const cleanDateStr = String(dateStr).replace(/^"|"$/g, '').trim();
          let date = moment(cleanDateStr, 'DD/MM/YYYY', true);
          if (!date.isValid()) {
            date = moment(cleanDateStr, 'DD-MM-YYYY', true);
          }
          if (!date.isValid()) {
            date = moment(cleanDateStr, 'DD/MM/YY', true);
          }
          if (!date.isValid()) return false;
          return date.isBetween(monthStart, monthEnd, 'day', '[]');
        });
        
        const aggregated = aggregateData(filtered, standardRatesMap, fullTankDataMap, fuelAddedMap, period);
        allPeriodData.push({
          period: period.format('YYYY-MM'),
          periodLabel: period.format('MMM YYYY'),
          data: aggregated
        });
      }
      
      // Merge data by asset for comparison
      const comparisonMap = {};
      allPeriodData.forEach(({ period, periodLabel, data }) => {
        data.forEach(asset => {
          const key = `${asset.assetCode}_${asset.assetType}_${asset.assetSubType}_${asset.assetModel}`;
          if (!comparisonMap[key]) {
            comparisonMap[key] = {
              assetCode: asset.assetCode,
              assetType: asset.assetType,
              assetSubType: asset.assetSubType,
              assetModel: asset.assetModel,
              ownerCode: asset.ownerCode,
              altName: asset.altName,
              periods: {}
            };
          }
          comparisonMap[key].periods[period] = {
            ...asset,
            periodLabel,
            location: asset.location,
            subLocation: asset.subLocation
          };
        });
      });
      
      setComparisonData(Object.values(comparisonMap));
      message.success(`Loaded comparison data for ${selectedPeriods.length} period(s)`);
      
      // Load full tank data from API independently in the background (non-blocking)
      const uniqueAssetCodes = [...new Set(allCsvData.map(row => getStringValue(row, 'Asset Code')).filter(Boolean))];
      console.log(`Found ${uniqueAssetCodes.length} unique asset codes for comparison, loading full tank data in background...`);
      
      if (uniqueAssetCodes.length > 0) {
        // Load full tank data asynchronously without blocking table display
        (async () => {
          try {
            const fullTankMap = {};
            const allRawEvents = []; // Collect all raw events from all periods
            
            for (const period of selectedPeriods) {
              const periodStart = period.clone().startOf('month').startOf('day').valueOf();
              const periodEnd = period.clone().endOf('month').endOf('day').valueOf();
              const monthKey = period.format('YYYY-MM');
              
              for (const assetCode of uniqueAssetCodes) {
                try {
                  const result = await fetchFullTankEventsForPeriod(assetCode, periodStart, periodEnd);
                  
                  if (result.success && result.events.length > 0) {
                    const assetMonthKey = `${assetCode}_${monthKey}`;
                    
                    // Filter events to only include those in the selected month
                    const monthEvents = result.events.filter(event => {
                      const eventDate = moment(event.date);
                      return eventDate.format('YYYY-MM') === monthKey;
                    });
                    
                    if (monthEvents.length > 0) {
                      // Fetch all fuel entries for this asset to calculate quantity consumed and include in breakdown
                      let allFuelEntries = [];
                      try {
                        const assetResult = await findAssetIdByCode(assetCode);
                        if (assetResult.success) {
                          const fuelEntriesResult = await fetchFuelEntries(
                            assetResult.assetId,
                            moment(periodStart).toISOString(),
                            moment(periodEnd).toISOString()
                          );
                          if (fuelEntriesResult.success) {
                            allFuelEntries = fuelEntriesResult.data || [];
                          }
                        }
                      } catch (error) {
                        console.warn(`Could not fetch fuel entries for ${assetCode}:`, error);
                      }
                      
                      // Sort all fuel entries by date (keep all entries, not just month entries)
                      // The API fetches previous full tank events to enable calculation for first event in range
                      const sortedAllFuelEntries = [...allFuelEntries].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
                      
                      // Filter all fuel entries to only include those in the selected month (for display)
                      const monthFuelEntries = sortedAllFuelEntries.filter(entry => {
                        const entryDate = moment(entry.date);
                        return entryDate.format('YYYY-MM') === monthKey;
                      });
                      
                      // Get standard consumption rate unit from asset model (if available)
                      let standardRateUnit = null;
                      if (monthEvents.length > 0 && monthEvents[0].assetModel) {
                        const assetModel = monthEvents[0].assetModel;
                        if (assetModel.standardFuelConsumptionRate && Array.isArray(assetModel.standardFuelConsumptionRate) && assetModel.standardFuelConsumptionRate.length > 0) {
                          const firstRate = assetModel.standardFuelConsumptionRate[0];
                          if (firstRate.perType && firstRate.perType.name) {
                            const unitName = firstRate.perType.name.toUpperCase();
                            if (unitName.includes('KM')) {
                              standardRateUnit = 'KM';
                            } else if (unitName.includes('HOUR')) {
                              standardRateUnit = 'HOURS';
                            } else if (unitName.includes('KWH')) {
                              standardRateUnit = 'KWH';
                            }
                          }
                        }
                      }
                      
                      // Transform ALL fuel entries (both full tank and non-full tank) to CSV-like format for breakdown view
                      // This is the same transformation logic as in loadFullTankDataFromApi
                      const transformedEvents = monthFuelEntries.map((event, entryIndex) => {
                        const isFullTank = event.isFullTank === true;
                        const fullTankIndex = isFullTank ? monthEvents.findIndex(e => 
                          (e._id === event._id) || (e.voucherNo === event.voucherNo && event.voucherNo)
                        ) : -1;
                        const eventDate = moment(event.date);
                        const dateStr = eventDate.format('DD-MM-YYYY');
                        const timeStr = eventDate.format('HH:mm:ss');
                        const dateTimeStr = `${dateStr}_${timeStr}`;
                        
                        // Extract meter readings
                        let kmsReading = null;
                        let hoursReading = null;
                        let kwhReading = null;
                        
                        if (event.mileage && Array.isArray(event.mileage)) {
                          event.mileage.forEach(meter => {
                            const meterName = (meter.name || '').toUpperCase();
                            if (meterName.includes('KM') || meterName.includes('ODO') || meterName.includes('DISTANCE')) {
                              kmsReading = meter.reading;
                            } else if (meterName.includes('HOUR') || meterName.includes('HRS')) {
                              hoursReading = meter.reading;
                            } else if (meterName.includes('KWH')) {
                              kwhReading = meter.reading;
                            }
                          });
                        }
                        
                        // Calculate consumption for full tank events
                        // Find previous full tank event from ALL fetched entries (not just month entries)
                        // This allows calculation for the first full tank event in the month
                        let quantityConsumed = null;
                        let kmsConsumed = null;
                        let hoursConsumed = null;
                        let kwhConsumed = null;
                        
                        if (isFullTank) {
                          const currentEventDate = moment(event.date);
                          
                          // Find the previous full tank event from ALL fetched entries (including those outside the month)
                          let previousEvent = null;
                          for (let i = sortedAllFuelEntries.length - 1; i >= 0; i--) {
                            const entry = sortedAllFuelEntries[i];
                            if (entry.isFullTank === true) {
                              const entryDate = moment(entry.date);
                              if (entryDate.isBefore(currentEventDate)) {
                                previousEvent = entry;
                                break;
                              }
                            }
                          }
                          
                          if (previousEvent) {
                            const previousEventDate = moment(previousEvent.date);
                            
                            // Fuel Consumed = Current full tank event quantity + all non-full tank events between previous and current
                            // Note: Previous full tank event quantity is NOT included
                            // Use ALL fetched entries (not just month entries) to include entries between previous and current
                            quantityConsumed = sortedAllFuelEntries
                              .filter(entry => {
                                const entryDate = moment(entry.date);
                                return (entryDate.isSame(currentEventDate) || 
                                        (entryDate.isAfter(previousEventDate) && entryDate.isBefore(currentEventDate)));
                              })
                              .reduce((sum, entry) => sum + (entry.quantity || 0), 0);
                            
                            // Use calculatedReading for run calculation (cumulative, handles meter resets)
                            if (event.mileage && Array.isArray(event.mileage) && 
                                previousEvent.mileage && Array.isArray(previousEvent.mileage)) {
                              event.mileage.forEach(currentMeter => {
                                const meterName = (currentMeter.name || '').toUpperCase();
                                const previousMeter = previousEvent.mileage.find(m => 
                                  (m.name || '').toUpperCase() === meterName
                                );
                                
                                // Use calculatedReading for accurate run calculation
                                const currentReading = getCalculatedReading(currentMeter);
                                const previousReading = getCalculatedReading(previousMeter);
                                
                                if (currentReading !== null && previousReading !== null) {
                                  const consumed = currentReading - previousReading;
                                  
                                  if (meterName.includes('KM') || meterName.includes('ODO') || meterName.includes('DISTANCE')) {
                                    kmsConsumed = consumed > 0 ? consumed : null;
                                  } else if (meterName.includes('HOUR') || meterName.includes('HRS')) {
                                    hoursConsumed = consumed > 0 ? consumed : null;
                                  } else if (meterName.includes('KWH')) {
                                    kwhConsumed = consumed > 0 ? consumed : null;
                                  }
                                }
                              });
                            }
                          }
                        }
                        
                        // Calculate consumption rates
                        let kmsPerQty = null;
                        let qtyPerKms = null;
                        let hoursPerQty = null;
                        let qtyPerHours = null;
                        
                        if (isFullTank) {
                          let consumptionRate = null;
                          let consumptionRateUnit = result.avgConsumptionRateUnit;
                          
                          if (fullTankIndex > 0 && result.consumptionRates && result.consumptionRates.length >= fullTankIndex) {
                            consumptionRate = result.consumptionRates[fullTankIndex - 1];
                          } else if (result.avgConsumptionRate && fullTankIndex > 0) {
                            consumptionRate = result.avgConsumptionRate;
                          }
                          
                          if (standardRateUnit === 'KM' && quantityConsumed > 0 && kmsConsumed > 0) {
                            kmsPerQty = kmsConsumed / quantityConsumed;
                          } else if (standardRateUnit === 'HOURS' && quantityConsumed > 0 && hoursConsumed > 0) {
                            qtyPerHours = quantityConsumed / hoursConsumed;
                          } else if (consumptionRate && consumptionRateUnit) {
                            if (consumptionRateUnit === 'KM/L') {
                              kmsPerQty = consumptionRate;
                            } else if (consumptionRateUnit === 'L/KM') {
                              qtyPerKms = consumptionRate;
                            } else if (consumptionRateUnit === 'L/HOURS') {
                              qtyPerHours = consumptionRate;
                            } else if (consumptionRateUnit === 'HOURS/L') {
                              hoursPerQty = consumptionRate;
                            }
                          } else if (quantityConsumed > 0) {
                            if (kmsConsumed > 0) {
                              kmsPerQty = kmsConsumed / quantityConsumed;
                            } else if (hoursConsumed > 0) {
                              qtyPerHours = quantityConsumed / hoursConsumed;
                            }
                          }
                        }
                        
                        return {
                          'Date': dateTimeStr,
                          'Asset': assetCode,
                          'Full Tank': isFullTank ? 'true' : 'false',
                          'Voucher': event.voucherNo || '',
                          'Quantity Refilled': event.quantity || 0,
                          'Quantity Consumed': quantityConsumed,
                          'KMS Reading': kmsReading,
                          'HOURS Reading': hoursReading,
                          'KWH Reading': kwhReading,
                          'KMS Consumed': kmsConsumed,
                          'HOURS Consumed': hoursConsumed,
                          'KWH Consumed': kwhConsumed,
                          'KMS/ Qty': kmsPerQty,
                          'Qty/ KMS': qtyPerKms,
                          'HOURS/ Qty': hoursPerQty,
                          'Qty/ HOURS': qtyPerHours,
                          _standardRateUnit: standardRateUnit,
                          _apiEvent: event
                        };
                      });
                      
                      // Add transformed events to the collection
                      allRawEvents.push(...transformedEvents);
                      
                      fullTankMap[assetMonthKey] = {
                        assetCode,
                        month: monthKey,
                        events: monthEvents.map(event => ({
                          date: moment(event.date).format('DD-MM-YYYY'),
                          consumptionRate: result.avgConsumptionRate,
                          consumptionRateUnit: result.avgConsumptionRateUnit
                        })),
                        consumptionRates: result.consumptionRates || []
                      };
                    }
                  }
                } catch (error) {
                  console.error(`Error loading full tank data for ${assetCode} in ${period.format('YYYY-MM')}:`, error);
                }
              }
            }
            
            // Set all raw events from all periods
            setFullTankRawData(allRawEvents);
            
            console.log(`Loaded full tank data from API for ${Object.keys(fullTankMap).length} asset-month combinations`);
            console.log(`Total raw events for breakdown: ${allRawEvents.length}`);
            setFullTankDataMap(fullTankMap);
            setFullTankDataLoaded(true);
            
            // Re-aggregate comparison data with updated full tank data
            const allPeriodDataUpdated = [];
            for (const period of selectedPeriods) {
              const monthStart = moment(period).startOf('month');
              const monthEnd = moment(period).endOf('month');
              
              const filtered = allCsvData.filter(row => {
                const dateStr = row['Date'] || row['"Date"'] || row['date'] || row['DATE'] || '';
                if (!dateStr) return false;
                const cleanDateStr = String(dateStr).replace(/^"|"$/g, '').trim();
                let date = moment(cleanDateStr, 'DD/MM/YYYY', true);
                if (!date.isValid()) {
                  date = moment(cleanDateStr, 'DD-MM-YYYY', true);
                }
                if (!date.isValid()) {
                  date = moment(cleanDateStr, 'DD/MM/YY', true);
                }
                if (!date.isValid()) return false;
                return date.isBetween(monthStart, monthEnd, 'day', '[]');
              });
              
              const aggregated = aggregateData(filtered, standardRatesMap, fullTankMap, fuelAddedMap, period);
              allPeriodDataUpdated.push({
                period: period.format('YYYY-MM'),
                periodLabel: period.format('MMM YYYY'),
                data: aggregated
              });
            }
            
            // Merge data by asset for comparison
            const comparisonMapUpdated = {};
            allPeriodDataUpdated.forEach(({ period, periodLabel, data }) => {
              data.forEach(asset => {
                const key = `${asset.assetCode}_${asset.assetType}_${asset.assetSubType}_${asset.assetModel}`;
                if (!comparisonMapUpdated[key]) {
                  comparisonMapUpdated[key] = {
                    assetCode: asset.assetCode,
                    assetType: asset.assetType,
                    assetSubType: asset.assetSubType,
                    assetModel: asset.assetModel,
                    ownerCode: asset.ownerCode,
                    altName: asset.altName,
                    periods: {}
                  };
                }
                comparisonMapUpdated[key].periods[period] = {
                  ...asset,
                  periodLabel,
                  location: asset.location,
                  subLocation: asset.subLocation
                };
              });
            });
            
            setComparisonData(Object.values(comparisonMapUpdated));
            message.success(`Updated comparison data with full tank information`);
          } catch (error) {
            console.error('Error loading full tank data in background for comparison:', error);
          }
        })();
      } else {
        setFullTankDataMap({});
        setFullTankDataLoaded(true);
      }
    } catch (error) {
      console.error('Error loading comparison data:', error);
      message.error('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const loadSummaryData = async () => {
    if (!selectedMonth) {
      message.warning('Please select a month');
      return;
    }
    
    setLoading(true);
    try {
      // Use API to fetch data for the selected month
      const monthStart = selectedMonth.clone().startOf('month');
      const monthEnd = selectedMonth.clone().endOf('month');
      const startTimestamp = monthStart.startOf('day').valueOf();
      const endTimestamp = monthEnd.endOf('day').valueOf();
      
      console.log('API Date range for month:', {
        month: selectedMonth.format('YYYY-MM'),
        start: monthStart.format('YYYY-MM-DD'),
        end: monthEnd.format('YYYY-MM-DD'),
        startTimestamp,
        endTimestamp
      });
      
      const result = await fetchDailyFuelDataReport(startTimestamp, endTimestamp, {
        assets: null,
        locations: ["all"],
        subLocations: ["all"],
        assetTypes: [],
        assetSubTypes: [],
        assetModels: [],
        frequency: "Daily"
      });
      
      if (!result.success) {
        message.error(`Failed to fetch data from API: ${result.error}`);
        setLoading(false);
        return;
      }
      
      console.log(`Successfully fetched ${result.data.length} daily records from API`);
      
      // Load fuel bowser map and asset type map
      const bowserMap = await loadFuelBowserMap();
      const typeMap = await loadAssetTypeMap();
      
      // Transform API response to CSV format (with fuel bowser filtering and asset type mapping)
      const csvData = transformApiResponseToCsvFormat(result.data, bowserMap, typeMap);
      console.log(`Transformed to ${csvData.length} CSV-like rows (after filtering fuel bowser assets)`);
      
      // Filter for selected month (reuse monthStart and monthEnd from above)
      console.log('Month filtering:', {
        month: selectedMonth.format('YYYY-MM'),
        monthStart: monthStart.format('YYYY-MM-DD'),
        monthEnd: monthEnd.format('YYYY-MM-DD')
      });
      
      const filtered = csvData.filter(row => {
        const dateStr = row['Date'] || row['"Date"'] || row['date'] || row['DATE'] || '';
        if (!dateStr) return false;
        
        const cleanDateStr = String(dateStr).replace(/^"|"$/g, '').trim();
        
        // Parse date - primary format is DD/MM/YYYY, with fallbacks
        let date = moment(cleanDateStr, 'DD/MM/YYYY', true);
        if (!date.isValid()) {
          date = moment(cleanDateStr, 'DD-MM-YYYY', true);
        }
        if (!date.isValid()) {
          date = moment(cleanDateStr, 'DD/MM/YY', true);
        }
        
        if (!date.isValid()) {
          return false;
        }
        
        // Filter by month
        return date.isBetween(monthStart, monthEnd, 'day', '[]');
      });
      
      console.log(`Filtered ${filtered.length} rows for month ${selectedMonth.format('MMMM YYYY')}`);
      
      // Store filtered daily data for breakdown view
      setDailyData(filtered);
      
      // For API mode, set fuelAddedDataLoaded to true (we don't use CSV consumption report)
      setFuelAddedDataLoaded(true);
      
      // Aggregate data by Asset (initially without full tank data)
      const aggregated = aggregateData(filtered, standardRatesMap, fullTankDataMap, fuelAddedMap, selectedMonth);
      
      setSummaryData(aggregated);
      setFilteredData(aggregated);
      
      // Extract unique values for filters
      const types = [...new Set(aggregated.map(item => item.assetType).filter(Boolean))].sort();
      const subTypes = [...new Set(aggregated.map(item => item.assetSubType).filter(Boolean))].sort();
      const models = [...new Set(aggregated.map(item => item.assetModel).filter(Boolean))].sort();
      const codes = [...new Set(aggregated.map(item => item.assetCode).filter(Boolean))].sort();
      const locs = [...new Set(aggregated.map(item => item.location).filter(Boolean))].sort();
      const subLocs = [...new Set(aggregated.map(item => item.subLocation).filter(Boolean))].sort();
      
      setAssetTypes(types);
      setAssetSubTypes(subTypes);
      setAssetModels(models);
      setAssetCodes(codes);
      setLocations(locs);
      setSubLocations(subLocs);
      
      // Create a map of asset codes with their owner codes and alt names for search
      const assetCodeMap = {};
      aggregated.forEach(item => {
        if (item.assetCode) {
          if (!assetCodeMap[item.assetCode]) {
            assetCodeMap[item.assetCode] = {
              assetCode: item.assetCode,
              ownerCode: item.ownerCode || '',
              altName: item.altName || ''
            };
          }
        }
      });
      setAssetCodeMap(assetCodeMap);
      
      message.success(`Loaded ${aggregated.length} asset summaries for ${selectedMonth.format('MMMM YYYY')}`);
      
      // Load full tank data from API independently in the background (non-blocking)
      const uniqueAssetCodes = [...new Set(csvData.map(row => getStringValue(row, 'Asset Code')).filter(Boolean))];
      console.log(`Found ${uniqueAssetCodes.length} unique asset codes, loading full tank data in background...`);
      
      if (uniqueAssetCodes.length > 0) {
        // Load full tank data asynchronously without blocking table display
        loadFullTankDataFromApi(uniqueAssetCodes, startTimestamp, endTimestamp, selectedMonth)
          .then((newFullTankMap) => {
            console.log('Full tank data loaded, updating table...');
            // Re-aggregate data with updated full tank data
            const aggregated = aggregateData(filtered, standardRatesMap, newFullTankMap, fuelAddedMap, selectedMonth);
            setSummaryData(aggregated);
            setFilteredData(aggregated);
            // Mark as loaded only AFTER re-aggregation completes
            setFullTankDataLoaded(true);
            // Now we can set loading to false since full tank data is loaded
            setLoading(false);
          })
          .catch(error => {
            console.error('Error loading full tank data in background:', error);
            // Re-aggregate with empty full tank map on error
            const aggregated = aggregateData(filtered, standardRatesMap, {}, fuelAddedMap, selectedMonth);
            setSummaryData(aggregated);
            setFilteredData(aggregated);
            setFullTankDataLoaded(true); // Mark as loaded even on error to prevent infinite loading
            // Set loading to false even on error
            setLoading(false);
          });
      } else {
        setFullTankDataMap({});
        setFullTankDataLoaded(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading summary data:', error);
      message.error('Failed to load summary data');
      setLoading(false);
      setFullTankDataLoaded(true); // Mark as loaded to prevent infinite spinner
    }
  };

  // Helper function to safely get string value from row (moved outside for reuse)
  const getStringValue = (row, fieldName) => {
    const value = row[fieldName] || row[`"${fieldName}"`] || '';
    return String(value).replace(/^"|"$/g, '').trim();
  };
  
  // Helper function to safely parse a numeric value from row (moved outside for reuse)
  const getNumericValue = (row, fieldName) => {
    // Try multiple field name variations
    let value = row[fieldName];
    if (value === undefined || value === null || value === '') {
      value = row[`"${fieldName}"`] || '';
    }
    
    // If already a number, return it
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }
    
    // If string, remove quotes and whitespace, then parse
    const cleanValue = String(value).replace(/^"|"$/g, '').trim();
    
    // Handle empty strings
    if (cleanValue === '' || cleanValue === 'null' || cleanValue === 'undefined') {
      return 0;
    }
    
    const numValue = parseFloat(cleanValue);
    
    // Return 0 if NaN, otherwise return the number
    if (isNaN(numValue)) {
      console.warn(`Failed to parse numeric value for field "${fieldName}":`, value, '→ returning 0');
      return 0;
    }
    
    return numValue;
  };

  const aggregateData = (data, standardRatesMap = {}, fullTankDataMap = {}, fuelAddedMap = {}, selectedMonth = moment()) => {
    const aggregationMap = {};
    
    data.forEach(row => {
      // Handle different field name formats (with/without quotes, different cases)
      const assetCode = getStringValue(row, 'Asset Code');
      const assetType = getStringValue(row, 'Asset Type');
      const assetSubType = getStringValue(row, 'Asset Sub Type');
      const assetModel = getStringValue(row, 'Asset Model');
      const ownerCode = getStringValue(row, 'Owner Code');
      const altName = getStringValue(row, 'Alt Name');
      const location = getStringValue(row, 'Location');
      const subLocation = getStringValue(row, 'Sub Location');
      
      // Create unique key
      const key = `${assetCode}_${assetType}_${assetSubType}_${assetModel}`;
      
      if (!aggregationMap[key]) {
        aggregationMap[key] = {
          snNo: 0, // Will be set later
          assetType,
          assetSubType,
          assetModel,
          assetCode,
          ownerCode,
          altName,
          location,
          subLocation,
          totalIgnition: 0,
          totalIdle: 0,
          totalMoving: 0,
          totalDataAvailable: 0,
          unitOfMeasurement: '',
          totalUnits: 0,
          totalFuelConsumed: 0,
          totalFuelAdded: 0,
          totalPossibleLeak: 0,
          totalManualRefuelQuantity: 0,
          consumptionRate: 0,
          standardConsumptionRate: null,
          standardConsumptionRateUnit: null,
          consumptionVariance: null,
          fuelLoss: null,
          fullTankRefuelCount: 0,
          fullTankAvgConsumptionRate: null,
          fullTankAvgConsumptionRateUnit: null,
          totalFuelAddedFromConsumptionReport: 0
        };
      }
      
      const record = aggregationMap[key];
      
      // Sum up values - handle different field name formats
      // First, let's log the actual field names from the first row to debug
      if (Object.keys(aggregationMap).length === 0) {
        console.log('=== CSV Field Mapping Debug ===');
        console.log('Available row keys:', Object.keys(row));
        console.log('Sample row values:', {
          'Date': row['Date'],
          'Ignition Time (min)': row['Ignition Time (min)'],
          'Idle Time (min)': row['Idle Time (min)'],
          'Moving Time (min)': row['Moving Time (min)'],
          'Distance Travelled (km)': row['Distance Travelled (km)'],
          'Fuel Consumed (ltrs)': row['Fuel Consumed (ltrs)'],
          'Possible Refuel': row['Possible Refuel'],
          'Possible Drain': row['Possible Drain']
        });
      }
      
      const ignitionTime = getNumericValue(row, 'Ignition Time (min)');
      const idleTime = getNumericValue(row, 'Idle Time (min)');
      const movingTime = getNumericValue(row, 'Moving Time (min)');
      const distanceTravelled = getNumericValue(row, 'Distance Travelled (km)');
      const fuelConsumed = getNumericValue(row, 'Fuel Consumed (ltrs)');
      const possibleRefuel = getNumericValue(row, 'Possible Refuel');
      const possibleDrain = getNumericValue(row, 'Possible Drain');
      const manualRefuelQuantity = getNumericValue(row, 'Manual Refuel Quantity');
      const dataAvailable = getNumericValue(row, 'Data Available (hours)');
      
      // Debug: Log values for first few rows
      if (Object.keys(aggregationMap).length < 3) {
        console.log(`Row ${Object.keys(aggregationMap).length + 1} - Raw CSV values:`, {
          'row["Ignition Time (min)"]': row['Ignition Time (min)'],
          'row["Idle Time (min)"]': row['Idle Time (min)'],
          'row["Moving Time (min)"]': row['Moving Time (min)'],
          'row["Distance Travelled (km)"]': row['Distance Travelled (km)'],
          'row["Fuel Consumed (ltrs)"]': row['Fuel Consumed (ltrs)'],
          'row["Possible Refuel"]': row['Possible Refuel'],
          'row["Possible Drain"]': row['Possible Drain'],
          'row["Data Available (hours)"]': row['Data Available (hours)']
        });
        console.log(`Row ${Object.keys(aggregationMap).length + 1} - Parsed values:`, {
          assetCode,
          ignitionTime,
          idleTime,
          movingTime,
          distanceTravelled,
          fuelConsumed,
          possibleRefuel,
          possibleDrain,
          dataAvailable
        });
      }
      
      // Ensure all values are numbers before adding
      const safeIgnition = typeof ignitionTime === 'number' && !isNaN(ignitionTime) ? ignitionTime : 0;
      const safeIdle = typeof idleTime === 'number' && !isNaN(idleTime) ? idleTime : 0;
      const safeMoving = typeof movingTime === 'number' && !isNaN(movingTime) ? movingTime : 0;
      const safeFuelConsumed = typeof fuelConsumed === 'number' && !isNaN(fuelConsumed) ? fuelConsumed : 0;
      const safeRefuel = typeof possibleRefuel === 'number' && !isNaN(possibleRefuel) ? possibleRefuel : 0;
      const safeDrain = typeof possibleDrain === 'number' && !isNaN(possibleDrain) ? possibleDrain : 0;
      const safeManualRefuel = typeof manualRefuelQuantity === 'number' && !isNaN(manualRefuelQuantity) ? manualRefuelQuantity : 0;
      const safeDataAvailable = typeof dataAvailable === 'number' && !isNaN(dataAvailable) ? dataAvailable : 0;
      
      record.totalIgnition += safeIgnition;
      record.totalIdle += safeIdle;
      record.totalMoving += safeMoving;
      record.totalFuelConsumed += safeFuelConsumed;
      record.totalFuelAdded += safeRefuel;
      record.totalPossibleLeak += safeDrain;
      record.totalManualRefuelQuantity += safeManualRefuel;
      record.totalDataAvailable += safeDataAvailable;
      
      // Debug: Track if any values are being skipped
      if (Object.keys(aggregationMap).length < 3) {
        console.log(`Row ${Object.keys(aggregationMap).length + 1} - Safe values being added:`, {
          ignition: safeIgnition,
          idle: safeIdle,
          moving: safeMoving,
          fuelConsumed: safeFuelConsumed,
          refuel: safeRefuel,
          drain: safeDrain,
          runningTotals: {
            totalIgnition: record.totalIgnition,
            totalIdle: record.totalIdle,
            totalMoving: record.totalMoving,
            totalFuelConsumed: record.totalFuelConsumed,
            totalFuelAdded: record.totalFuelAdded,
            totalPossibleLeak: record.totalPossibleLeak
          }
        });
      }
      
      // Determine unit of measurement - check asset type first for equipment, then standard rates
      if (!record.unitOfMeasurement) {
        // First, check asset type and sub type for equipment that should always be HOURS
        const assetTypeLower = assetType ? assetType.toLowerCase() : '';
        const assetSubTypeLower = assetSubType ? assetSubType.toLowerCase() : '';
        const combinedType = `${assetTypeLower} ${assetSubTypeLower}`;
        const isEquipment = combinedType.includes('excavator') || 
                           combinedType.includes('digger') || 
                           combinedType.includes('loader') || 
                           combinedType.includes('compressor') || 
                           combinedType.includes('generator') || 
                           combinedType.includes('dg') || 
                           combinedType.includes('dozer') ||
                           combinedType.includes('crane') ||
                           combinedType.includes('grader') ||
                           combinedType.includes('backhoe');
        
        if (isEquipment) {
          record.unitOfMeasurement = 'HOURS';
          // Still try to get standard rate if available
          if (assetModel && standardRatesMap) {
            // Try exact match first, then normalized (case-insensitive) match
            const normalizedModelName = assetModel.trim().toLowerCase();
            const stdRate = standardRatesMap[assetModel] || standardRatesMap[normalizedModelName];
            if (stdRate && stdRate.ltrPerHour) {
              record.standardConsumptionRate = stdRate.ltrPerHour;
              record.standardConsumptionRateUnit = 'Ltr/Hrs';
            } else if (Object.keys(aggregationMap).length < 3) {
              // Debug: Log when rate not found for first few records
              console.log(`Standard rate not found for equipment model: "${assetModel}" (normalized: "${normalizedModelName}")`);
            }
          }
        } else {
          // For non-equipment, check standard consumption rate first
        if (assetModel && standardRatesMap) {
          // Try exact match first, then normalized (case-insensitive) match
          const normalizedModelName = assetModel.trim().toLowerCase();
          const stdRate = standardRatesMap[assetModel] || standardRatesMap[normalizedModelName];
          if (stdRate) {
              // Prioritize ltrPerHour over kmsPerLtr when both are present
              // (e.g., generators may have both "16 L/HOURS" and "0.1 KwH/L")
              if (stdRate.ltrPerHour) {
              record.unitOfMeasurement = 'HOURS';
              record.standardConsumptionRate = stdRate.ltrPerHour;
              record.standardConsumptionRateUnit = 'Ltr/Hrs';
              } else if (stdRate.kmsPerLtr) {
                record.unitOfMeasurement = 'KMS';
                record.standardConsumptionRate = stdRate.kmsPerLtr;
                record.standardConsumptionRateUnit = 'KMS/Ltr';
            }
          } else if (Object.keys(aggregationMap).length < 3) {
            // Debug: Log when rate not found for first few records
            console.log(`Standard rate not found for model: "${assetModel}" (normalized: "${normalizedModelName}")`);
            console.log(`Available model keys (first 10):`, Object.keys(standardRatesMap).slice(0, 10));
          }
        }
        
        // If standard rate not found, fall back to asset type logic
        if (!record.unitOfMeasurement) {
            if (assetType && (assetTypeLower.includes('truck') || 
                assetTypeLower.includes('vehicle') || 
                assetTypeLower.includes('dumper'))) {
            record.unitOfMeasurement = 'KMS';
          } else {
            record.unitOfMeasurement = 'HOURS';
            }
          }
        }
      }
      
      // Add to total units - ensure numeric values, based on determined unit
      const safeDistance = typeof distanceTravelled === 'number' && !isNaN(distanceTravelled) ? distanceTravelled : 0;
      const safeIgnitionForUnits = typeof ignitionTime === 'number' && !isNaN(ignitionTime) ? ignitionTime : 0;
      
      if (record.unitOfMeasurement === 'KMS') {
        record.totalUnits += safeDistance;
      } else {
        // For equipment, use ignition time as hours
        record.totalUnits += (safeIgnitionForUnits / 60);
      }
    });
    
    // Calculate totals and consumption rates
    const aggregated = Object.values(aggregationMap).map((record, index) => {
      record.snNo = index + 1;
      
      // Get full tank refueling data for this asset and month
      const monthKey = selectedMonth.format('YYYY-MM');
      const assetMonthKey = `${record.assetCode}_${monthKey}`;
      const fullTankData = fullTankDataMap[assetMonthKey];
      
      // Get total fuel added from consumption report (all events - both full tank and non-full tank)
      // Also use aggregated manual refuel quantity from daily IoT data
      const fuelAddedData = fuelAddedMap[assetMonthKey];
      if (fuelAddedData) {
        record.totalFuelAddedFromConsumptionReport = fuelAddedData.totalFuelAdded;
      } else {
        // Fall back to aggregated manual refuel quantity from daily IoT data
        record.totalFuelAddedFromConsumptionReport = record.totalManualRefuelQuantity || 0;
      }
      
      // If we have manual refuel quantity from daily data, prefer it over consumption report
      // (consumption report might be more comprehensive, but daily data is more direct)
      if (record.totalManualRefuelQuantity > 0) {
        record.totalFuelAddedFromConsumptionReport = record.totalManualRefuelQuantity;
      }
      
      // Debug: Log first few lookups
      if (index < 3) {
        console.log(`Looking up full tank data for: ${assetMonthKey}, Found: ${fullTankData ? 'Yes' : 'No'}`);
        if (fullTankData) {
          console.log(`  Events: ${fullTankData.events.length}, Rates: ${fullTankData.consumptionRates.length}`);
        }
        console.log(`Looking up fuel added data for: ${assetMonthKey}, Found: ${fuelAddedData ? 'Yes' : 'No'}, Total: ${record.totalFuelAddedFromConsumptionReport}`);
      }
      
      if (fullTankData && fullTankData.events.length > 0) {
        record.fullTankRefuelCount = fullTankData.events.length;
        
        // Calculate average consumption rate from full tank events
        if (fullTankData.consumptionRates.length > 0) {
          const sum = fullTankData.consumptionRates.reduce((a, b) => a + b, 0);
          record.fullTankAvgConsumptionRate = sum / fullTankData.consumptionRates.length;
          // Use the unit from the first event (assuming all events have same unit)
          record.fullTankAvgConsumptionRateUnit = fullTankData.events[0].consumptionRateUnit;
        }
      } else {
        record.fullTankRefuelCount = 0;
        record.fullTankAvgConsumptionRate = null;
        record.fullTankAvgConsumptionRateUnit = null;
      }
      
        // Calculate Consumption Rate - must match standard rate unit format
        if (record.totalFuelConsumed > 0) {
          if (record.unitOfMeasurement === 'KMS') {
            // For KMS: calculate as KMS per Liter (to match KMS/Ltr standard)
            record.consumptionRate = record.totalUnits / record.totalFuelConsumed;
          } else {
            // For HOURS: calculate based on standard rate unit
            if (record.standardConsumptionRateUnit === 'Ltr/Hrs') {
              // Standard is Ltr/Hrs, so calculate actual as Ltr/Hrs
              // Ltr/Hrs = Total Fuel Consumed / Total Hours
              record.consumptionRate = record.totalFuelConsumed / record.totalUnits;
            } else {
              // No standard or standard is in different unit, calculate as Hrs/Ltr
              record.consumptionRate = record.totalUnits / record.totalFuelConsumed;
            }
          }
        } else {
          record.consumptionRate = 0;
        }
        
        // Calculate consumption variance and fuel loss if standard rate exists
        if (record.standardConsumptionRate && record.consumptionRate > 0 && record.totalUnits > 0) {
          if (record.standardConsumptionRateUnit === 'KMS/Ltr' && record.unitOfMeasurement === 'KMS') {
            // Both in KMS/Ltr
            // Variance = ((actual - standard) / standard) * 100
            record.consumptionVariance = ((record.consumptionRate - record.standardConsumptionRate) / record.standardConsumptionRate) * 100;
            
            // Fuel Loss calculation for KMS/Ltr:
            // Lower reported rate = more fuel consumed = fuel loss (positive)
            // Higher reported rate = less fuel consumed = fuel saved (negative)
            // Expected Fuel = Total Units / Standard Rate (KMS / (KMS/Ltr) = Ltr)
            // Actual Fuel = Total Units / Actual Rate (KMS / (KMS/Ltr) = Ltr)
            // OR use Total Fuel Consumed directly
            const expectedFuel = record.totalUnits / record.standardConsumptionRate;
            const actualFuel = record.totalFuelConsumed;
            // Positive = loss (actual > expected), Negative = saved (actual < expected)
            record.fuelLoss = actualFuel - expectedFuel;
          } else if (record.standardConsumptionRateUnit === 'Ltr/Hrs' && record.unitOfMeasurement === 'HOURS') {
            // Both in Ltr/Hrs
            // Variance = ((actual - standard) / standard) * 100
            record.consumptionVariance = ((record.consumptionRate - record.standardConsumptionRate) / record.standardConsumptionRate) * 100;
            
            // Fuel Loss calculation for Ltr/Hrs:
            // Higher reported rate = more fuel consumed = fuel loss (positive)
            // Lower reported rate = less fuel consumed = fuel saved (negative)
            // Expected Fuel = Total Hours * Standard Rate (Hrs * (Ltr/Hrs) = Ltr)
            // Actual Fuel = Total Fuel Consumed
            const expectedFuel = record.totalUnits * record.standardConsumptionRate;
            const actualFuel = record.totalFuelConsumed;
            // Positive = loss (actual > expected), Negative = saved (actual < expected)
            record.fuelLoss = actualFuel - expectedFuel;
          }
        }
      
      // Debug: Log first aggregated record
      if (index === 0) {
        console.log('=== First Aggregated Record ===');
        console.log('Field Mappings:');
        console.log('  Total Ignition = sum of "Ignition Time (min)"');
        console.log('  Total IDLE = sum of "Idle Time (min)"');
        console.log('  Total MOVING = sum of "Moving Time (min)"');
        console.log('  Data Availability = (Total Data Available / (Days in month × 24)) × 100');
        console.log('  Total Units = sum of "Distance Travelled (km)" for vehicles OR sum of (Ignition Time / 60) for equipment');
        console.log('  Total FUEL CONSUMED = sum of "Fuel Consumed (ltrs)"');
        console.log('  Total FUEL ADDED = sum of "Possible Refuel"');
        console.log('  Total Possible Leak = sum of "Possible Drain"');
        console.log('Actual values:', {
          totalIgnition: record.totalIgnition,
          totalIdle: record.totalIdle,
          totalMoving: record.totalMoving,
          totalDataAvailable: record.totalDataAvailable,
          dataAvailabilityPercentage: ((record.totalDataAvailable / (selectedMonth.daysInMonth() * 24)) * 100).toFixed(2),
          totalUnits: record.totalUnits,
          totalFuelConsumed: record.totalFuelConsumed,
          totalFuelAdded: record.totalFuelAdded,
          totalPossibleLeak: record.totalPossibleLeak,
          unitOfMeasurement: record.unitOfMeasurement,
          consumptionRate: record.consumptionRate
        });
        console.log('Full record:', JSON.stringify(record, null, 2));
      }
      
      return record;
    });
    
    return aggregated;
  };

  const applyFilters = () => {
    let filtered = [...summaryData];
    
    if (selectedAssetCode) {
      filtered = filtered.filter(item => item.assetCode === selectedAssetCode);
    }
    
    if (selectedAssetType) {
      filtered = filtered.filter(item => item.assetType === selectedAssetType);
    }
    
    if (selectedAssetSubType) {
      filtered = filtered.filter(item => item.assetSubType === selectedAssetSubType);
    }
    
    if (selectedAssetModel) {
      filtered = filtered.filter(item => item.assetModel === selectedAssetModel);
    }
    
    if (selectedLocation) {
      filtered = filtered.filter(item => item.location === selectedLocation);
    }
    
    if (selectedSubLocation) {
      filtered = filtered.filter(item => item.subLocation === selectedSubLocation);
    }
    
    // Re-number SN
    filtered = filtered.map((item, index) => ({
      ...item,
      snNo: index + 1
    }));
    
    setFilteredData(filtered);
  };

  const handleDownloadPDF = async () => {
    try {
      // Transform comparison data if in comparison mode
      let dataToExport = filteredData;
      if (comparisonMode) {
        // Apply filters to comparison data (similar to ComparisonTable)
        let filtered = comparisonData;
        if (selectedAssetCode) {
          filtered = filtered.filter(item => item.assetCode === selectedAssetCode);
        }
        if (selectedAssetType) {
          filtered = filtered.filter(item => item.assetType === selectedAssetType);
        }
        if (selectedAssetSubType) {
          filtered = filtered.filter(item => item.assetSubType === selectedAssetSubType);
        }
        if (selectedAssetModel) {
          filtered = filtered.filter(item => item.assetModel === selectedAssetModel);
        }
        if (selectedLocation) {
          filtered = filtered.filter(item => {
            return selectedPeriods.some(period => {
              const periodKey = period.format('YYYY-MM');
              const periodData = item.periods[periodKey];
              return periodData && periodData.location === selectedLocation;
            });
          });
        }
        if (selectedSubLocation) {
          filtered = filtered.filter(item => {
            return selectedPeriods.some(period => {
              const periodKey = period.format('YYYY-MM');
              const periodData = item.periods[periodKey];
              return periodData && periodData.subLocation === selectedSubLocation;
            });
          });
        }
        
        // Transform comparisonData to flat structure similar to ComparisonTable
        const comparisonRows = [];
        filtered.forEach(asset => {
          selectedPeriods.forEach(period => {
            const periodKey = period.format('YYYY-MM');
            const periodData = asset.periods[periodKey];
            
            if (periodData) {
              comparisonRows.push({
                ...periodData,
                periodLabel: period.format('MMM YYYY'),
                periodKey: periodKey,
                periodMoment: period
              });
            }
          });
        });
        dataToExport = comparisonRows;
      }
      
      if (!dataToExport || dataToExport.length === 0) {
        message.warning('No data available to export');
        return;
      }
      
      // Create PDF in landscape mode
      const doc = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth(); // ~842pt for A4 landscape
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;
      
      // Calculate available width for table (page width minus margins)
      const tableMargin = 30; // 15pt on each side
      const availableWidth = pageWidth - tableMargin;
      
      // Load and add logo to top left corner
      const loadLogo = () => {
        return new Promise((resolve) => {
          const logoPath = '/Logo-svg copy.png';
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          
          logoImg.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = logoImg.width;
              canvas.height = logoImg.height;
              ctx.drawImage(logoImg, 0, 0);
              const logoDataUrl = canvas.toDataURL('image/png');
              
              // Calculate aspect ratio to maintain fixed aspect ratio
              const aspectRatio = logoImg.width / logoImg.height;
              const logoHeight = 40; // Fixed height
              const logoWidth = logoHeight * aspectRatio; // Maintain aspect ratio
              
              // Add logo at top left with fixed aspect ratio
              doc.addImage(logoDataUrl, 'PNG', 20, 15, logoWidth, logoHeight);
              resolve();
            } catch (error) {
              console.warn('Could not add logo to PDF:', error);
              resolve();
            }
          };
          
          logoImg.onerror = () => {
            console.warn('Could not load logo image');
            resolve();
          };
          
          logoImg.src = logoPath;
        });
      };
      
      // Load logo synchronously before continuing
      await loadLogo();
      
      // Header Section with Logo and Title
      const headerY = 25;
      
      // Title - Right aligned to leave space for logo
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      const title = `Sensor-Based Fuel & Utilization Report${comparisonMode ? ' - Comparison' : ` - ${selectedMonth.format('MMMM YYYY')}`}`;
      doc.text(title, pageWidth - 20, headerY, { align: 'right' });
      
      // Company/Organization Name - Below title
      try {
        const userData = getUserData();
        if (userData?.organisation) {
          const companyName = typeof userData.organisation === 'string' 
            ? userData.organisation 
            : (userData.organisation.name || '');
          if (companyName) {
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(companyName, pageWidth - 20, headerY + 20, { align: 'right' });
            doc.setTextColor(0, 0, 0);
          }
        }
      } catch (error) {
        console.warn('Could not retrieve company name:', error);
      }
      
      yPosition = headerY + 45;
      
      // Calculate totals from dataToExport
      const calculatedTotals = dataToExport.reduce((acc, item) => {
        acc.totalIgnition += item.totalIgnition || 0;
        acc.totalIdle += item.totalIdle || 0;
        acc.totalMoving += item.totalMoving || 0;
        acc.totalDataAvailable += item.totalDataAvailable || 0;
        acc.totalUnits += item.totalUnits || 0;
        acc.totalFuelConsumed += item.totalFuelConsumed || 0;
        acc.totalFuelAdded += item.totalFuelAdded || 0;
        acc.totalPossibleLeak += item.totalPossibleLeak || 0;
        acc.totalFuelAddedManualEntry += (item.totalFuelAddedFromConsumptionReport || item.totalManualRefuelQuantity || 0);
        acc.totalFuelLoss += item.fuelLoss || 0;
        return acc;
      }, {
        totalIgnition: 0,
        totalIdle: 0,
        totalMoving: 0,
        totalDataAvailable: 0,
        totalUnits: 0,
        totalFuelConsumed: 0,
        totalFuelAdded: 0,
        totalPossibleLeak: 0,
        totalFuelAddedManualEntry: 0,
        totalFuelLoss: 0
      });
      
      // Calculate difference and percentage for PDF summary
      const pdfFuelAddedDifference = calculatedTotals.totalFuelAdded - calculatedTotals.totalFuelAddedManualEntry;
      const pdfFuelAddedDifferencePercentage = calculatedTotals.totalFuelAddedManualEntry > 0 
        ? ((pdfFuelAddedDifference / calculatedTotals.totalFuelAddedManualEntry) * 100)
        : (pdfFuelAddedDifference !== 0 ? (pdfFuelAddedDifference > 0 ? 100 : -100) : 0);
      
      // Summary Statistics Cards - Card-style design
      yPosition += 10;
      const cardHeight = 50;
      const startX = 20;
      const endX = pageWidth - 20; // Right margin
      const totalCardArea = endX - startX; // Total width available for cards
      const cardSpacing = 15;
      const cardWidth = (totalCardArea - (cardSpacing * 3)) / 4; // 4 cards with 3 spacings between them
      
      // Card colors - light backgrounds
      const cardColors = [
        [240, 248, 255], // Light blue for Total Fuel Consumed
        [240, 255, 240], // Light green for Total Fuel Added
        [255, 245, 238], // Light orange for Total Possible Leak
        [255, 240, 245]  // Light pink for Total Fuel Lost
      ];
      
      // Card 1: Total Fuel Consumed
      doc.setFillColor(...cardColors[0]);
      doc.roundedRect(startX, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(startX, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Total Fuel Consumed', startX + 10, yPosition + 12);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${calculatedTotals.totalFuelConsumed.toFixed(2)} ltrs`, startX + 10, yPosition + 30);
      
      // Card 2: Total Fuel Added
      doc.setFillColor(...cardColors[1]);
      doc.roundedRect(startX + cardWidth + cardSpacing, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(startX + cardWidth + cardSpacing, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Total Fuel Added', startX + cardWidth + cardSpacing + 10, yPosition + 12);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${calculatedTotals.totalFuelAdded.toFixed(2)} ltrs`, startX + cardWidth + cardSpacing + 10, yPosition + 30);
      
      // Card 3: Total Possible Leak
      doc.setFillColor(...cardColors[2]);
      doc.roundedRect(startX + (cardWidth + cardSpacing) * 2, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(startX + (cardWidth + cardSpacing) * 2, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Total Possible Leak', startX + (cardWidth + cardSpacing) * 2 + 10, yPosition + 12);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${calculatedTotals.totalPossibleLeak.toFixed(2)} ltrs`, startX + (cardWidth + cardSpacing) * 2 + 10, yPosition + 30);
      
      // Card 4: Total Fuel Lost
      doc.setFillColor(...cardColors[3]);
      doc.roundedRect(startX + (cardWidth + cardSpacing) * 3, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(startX + (cardWidth + cardSpacing) * 3, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      const fuelLossLabel = calculatedTotals.totalFuelLoss >= 0 ? 'Total Fuel Lost' : 'Total Fuel Saved';
      doc.text(fuelLossLabel, startX + (cardWidth + cardSpacing) * 3 + 10, yPosition + 12);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${Math.abs(calculatedTotals.totalFuelLoss).toFixed(2)} ltrs`, startX + (cardWidth + cardSpacing) * 3 + 10, yPosition + 30);
      
      yPosition += cardHeight + 20;
      
      // Additional info row below cards
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      const additionalInfo = [
        `Total Fuel Added Manual Entry: ${calculatedTotals.totalFuelAddedManualEntry.toFixed(2)} ltrs`,
        `Difference (Sensor - Manual): ${pdfFuelAddedDifference.toFixed(2)} ltrs (${pdfFuelAddedDifferencePercentage >= 0 ? '+' : ''}${pdfFuelAddedDifferencePercentage.toFixed(2)}%)`
      ];
      doc.text(additionalInfo[0], startX, yPosition);
      doc.text(additionalInfo[1], startX + cardWidth * 2, yPosition);
      doc.setTextColor(0, 0, 0);
      yPosition += 20;
      
      yPosition += 20;
      
      // Prepare table data with record metadata for color coding
      const tableDataWithMetadata = dataToExport.map((record, index) => {
        const monthForCalc = record.periodMoment || (record.periodKey ? moment(record.periodKey, 'YYYY-MM') : selectedMonth);
        const daysInMonth = monthForCalc.daysInMonth();
        const totalPossibleHours = daysInMonth * 24;
        const dataAvailabilityPercentage = totalPossibleHours > 0 
          ? parseFloat(((record.totalDataAvailable / totalPossibleHours) * 100).toFixed(2))
          : 0;
        const idlePercentage = record.totalIgnition > 0 
          ? parseFloat(((record.totalIdle / record.totalIgnition) * 100).toFixed(2))
          : 0;
        const movingPercentage = record.totalIgnition > 0 
          ? parseFloat(((record.totalMoving / record.totalIgnition) * 100).toFixed(2))
          : 0;
        
        let consumptionRateUnit = '';
        if (record.consumptionRate > 0) {
          if (record.standardConsumptionRateUnit) {
            consumptionRateUnit = record.standardConsumptionRateUnit;
          } else {
            consumptionRateUnit = record.unitOfMeasurement === 'KMS' ? 'KMS/Ltr' : 'Hrs/Ltr';
          }
        }
        
        const totalFuelAdded = record.totalFuelAdded || 0;
        const manualEntry = record.totalFuelAddedFromConsumptionReport || 0;
        const difference = totalFuelAdded - manualEntry;
        const differencePercentage = manualEntry > 0 ? ((difference / manualEntry) * 100) : (difference !== 0 ? (difference > 0 ? 100 : -100) : 0);
        
        // Format Difference column with percentage on second line
        const differenceText = `${difference.toFixed(2)} ltrs\n${differencePercentage >= 0 ? '+' : ''}${differencePercentage.toFixed(2)}%`;
        
        const baseRow = [
            record.assetCode || '',
            record.assetType || '',
            record.assetSubType || '',
            record.assetModel || '',
            record.ownerCode || '',
            record.altName || '',
            `${record.totalIgnition.toFixed(2)} min`,
            `${record.totalIdle.toFixed(2)} min (${idlePercentage.toFixed(2)}%)`,
            `${record.totalMoving.toFixed(2)} min (${movingPercentage.toFixed(2)}%)`,
            `${record.totalDataAvailable.toFixed(2)} hrs (${dataAvailabilityPercentage.toFixed(2)}%)`,
            `${record.totalUnits.toFixed(2)} ${record.unitOfMeasurement || ''}`,
            `${record.totalFuelConsumed.toFixed(2)} ltrs`,
            `${record.totalFuelAdded.toFixed(2)} ltrs`,
            `${(record.totalFuelAddedFromConsumptionReport || 0).toFixed(2)} ltrs`,
            differenceText,
            `${record.totalPossibleLeak.toFixed(2)} ltrs`,
            record.consumptionRate > 0 ? `${record.consumptionRate.toFixed(2)} ${consumptionRateUnit}` : '0.00',
            record.standardConsumptionRate ? `${record.standardConsumptionRate.toFixed(2)} ${record.standardConsumptionRateUnit || ''}` : 'N/A',
            record.consumptionVariance !== null && record.consumptionVariance !== undefined ? `${record.consumptionVariance >= 0 ? '+' : ''}${record.consumptionVariance.toFixed(2)}%` : 'N/A',
            record.fuelLoss !== null && record.fuelLoss !== undefined ? `${record.fuelLoss >= 0 ? '+' : ''}${record.fuelLoss.toFixed(2)} ltrs` : 'N/A',
            record.fullTankRefuelCount || 0,
            record.fullTankAvgConsumptionRate ? `${record.fullTankAvgConsumptionRate.toFixed(2)} ${record.fullTankAvgConsumptionRateUnit || ''}` : 'N/A'
        ];
        
        // Add Period or Sn No. at the beginning
        const row = comparisonMode 
          ? [record.periodLabel || record.periodKey || '', ...baseRow]
          : [record.snNo || index + 1, ...baseRow];
        
        return {
          row: row,
          metadata: {
            idlePercentage,
            dataAvailabilityPercentage,
            consumptionVariance: record.consumptionVariance,
            fuelLoss: record.fuelLoss,
            standardConsumptionRateUnit: record.standardConsumptionRateUnit,
            differencePercentage: differencePercentage
          }
        };
      });
      
      const tableData = tableDataWithMetadata.map(item => item.row);
      
      // Table columns - add Period column in comparison mode
      const tableColumns = comparisonMode 
        ? [
            'Period',
            'Asset Code',
            'Asset Type',
            'Asset Sub Type',
            'Asset Model',
            'Owner Code',
            'Alt Name',
            'Total Ignition',
            'Total IDLE',
            'Total MOVING',
            'Data Availability',
            'Total Units',
            'Total Fuel Consumed',
            'Total Fuel Added',
            'Total Fuel Added (Manual)',
            'Diff (Sen - Man)',
            'Total Possible Leak',
            'Report Consumption Rate',
            'Standard Consumption Rate',
            'Variance',
            'Fuel Loss',
            'Full Tank Refuel Count',
            'Full Tank Avg Consumption Rate'
          ]
        : [
        'Sn No.',
        'Asset Code',
        'Asset Type',
        'Asset Sub Type',
        'Asset Model',
        'Owner Code',
        'Alt Name',
        'Total Ignition',
        'Total IDLE',
        'Total MOVING',
        'Data Availability',
        'Total Units',
        'Total Fuel Consumed',
        'Total Fuel Added',
        'Total Fuel Added (Manual)',
        'Diff (Sen - Man)',
        'Total Possible Leak',
        'Report Consumption Rate',
        'Standard Consumption Rate',
        'Variance',
        'Fuel Loss',
        'Full Tank Refuel Count',
        'Full Tank Avg Consumption Rate'
      ];
      
      // Table Title
      yPosition += 10;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Asset-wise Monthly Summary', 20, yPosition);
      yPosition += 20;
      
      // Add table using autoTable with color coding
      autoTable(doc, {
        startY: yPosition,
        head: [tableColumns],
        body: tableData,
        theme: 'striped',
        styles: { 
          fontSize: 5, 
          cellPadding: { top: 4, bottom: 4, left: 6, right: 6 },
          overflow: 'linebreak',
          cellWidth: 'auto',
          halign: 'left',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [220, 220, 220]
        },
        headStyles: { 
          fillColor: [52, 73, 94], 
          textColor: 255, 
          fontStyle: 'bold', 
          fontSize: 5.5,
          overflow: 'linebreak',
          cellWidth: 'auto',
          halign: 'center',
          valign: 'middle',
          minCellHeight: 15,
          cellPadding: { top: 6, bottom: 6, left: 6, right: 6 }
        },
        bodyStyles: {
          fontSize: 5,
          overflow: 'linebreak',
          cellWidth: 'auto',
          halign: 'left',
          valign: 'middle',
          cellPadding: { top: 4, bottom: 4, left: 6, right: 6 },
          minCellHeight: 12
        },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        margin: { left: 15, right: 15 },
        tableWidth: availableWidth,
        // Column styles with fixed widths for all columns
        // Both comparison and single mode use the same widths
        columnStyles: comparisonMode ? {
          // Comparison mode has Period column at index 0
          0: { cellWidth: 25, halign: 'left' },  // Period
          1: { cellWidth: 30, halign: 'left' },  // Asset Code
          2: { cellWidth: 35, halign: 'left' },  // Asset Type
          3: { cellWidth: 40, halign: 'left' },  // Asset Sub Type
          4: { cellWidth: 40, halign: 'left' },  // Asset Model
          5: { cellWidth: 30, halign: 'left' },  // Owner Code
          6: { cellWidth: 30, halign: 'left' },  // Alt Name
          7: { cellWidth: 35, halign: 'right' }, // Total Ignition
          8: { cellWidth: 35, halign: 'right' }, // Total IDLE
          9: { cellWidth: 35, halign: 'right' }, // Total MOVING
          10: { cellWidth: 30, halign: 'right' }, // Data Availability
          11: { cellWidth: 35, halign: 'right' }, // Total Units
          12: { cellWidth: 40, halign: 'right' }, // Total Fuel Consumed
          13: { cellWidth: 35, halign: 'right' }, // Total Fuel Added
          14: { cellWidth: 40, halign: 'right' }, // Total Fuel Added (Manual)
          15: { cellWidth: 30, halign: 'right' }, // Diff (Sen - Man)
          16: { cellWidth: 30, halign: 'right' }, // Total Possible Leak
          17: { cellWidth: 45, halign: 'right' }, // Report Consumption Rate
          18: { cellWidth: 40, halign: 'right' }, // Standard Consumption Rate
          19: { cellWidth: 35, halign: 'right' }, // Variance
          20: { cellWidth: 30, halign: 'left' },  // Fuel Loss
          21: { cellWidth: 35, halign: 'center' }, // Full Tank Refuel Count
          22: { cellWidth: 50, halign: 'right' }  // Full Tank Avg Consumption Rate
        } : {
          // Single mode - no Period column
          0: { cellWidth: 20, halign: 'left' },  // Sn No.
          1: { cellWidth: 30, halign: 'left' },  // Asset Code
          2: { cellWidth: 35, halign: 'left' },  // Asset Type
          3: { cellWidth: 40, halign: 'left' },  // Asset Sub Type
          4: { cellWidth: 40, halign: 'left' },  // Asset Model
          5: { cellWidth: 30, halign: 'left' },  // Owner Code
          6: { cellWidth: 30, halign: 'left' },  // Alt Name
          7: { cellWidth: 35, halign: 'right' }, // Total Ignition
          8: { cellWidth: 35, halign: 'right' }, // Total IDLE
          9: { cellWidth: 35, halign: 'right' }, // Total MOVING
          10: { cellWidth: 30, halign: 'right' }, // Data Availability
          11: { cellWidth: 35, halign: 'right' }, // Total Units
          12: { cellWidth: 40, halign: 'right' }, // Total Fuel Consumed
          13: { cellWidth: 35, halign: 'right' }, // Total Fuel Added
          14: { cellWidth: 40, halign: 'right' }, // Total Fuel Added (Manual)
          15: { cellWidth: 30, halign: 'right' }, // Diff (Sen - Man)
          16: { cellWidth: 30, halign: 'right' }, // Total Possible Leak
          17: { cellWidth: 45, halign: 'right' }, // Report Consumption Rate
          18: { cellWidth: 40, halign: 'right' }, // Standard Consumption Rate
          19: { cellWidth: 35, halign: 'right' }, // Variance
          20: { cellWidth: 30, halign: 'left' },  // Fuel Loss
          21: { cellWidth: 35, halign: 'center' }, // Full Tank Refuel Count
          22: { cellWidth: 50, halign: 'right' }  // Full Tank Avg Consumption Rate
        },
        didParseCell: function(data) {
          // Skip non-body rows (header, footer)
          if (data.row.section !== 'body') return;
          
          // In jsPDF-autoTable, data.row.index is 0-based for body rows
          const rowIndex = data.row.index;
          if (rowIndex >= 0 && rowIndex < tableDataWithMetadata.length) {
            const metadata = tableDataWithMetadata[rowIndex].metadata;
            const columnIndex = data.column.index;
            // Adjust column indices for comparison mode (Period column shifts everything by 1)
            // Note: Difference column added after Total Fuel Added (Manual), so indices after column 14 are shifted by 1
            const idleColIndex = comparisonMode ? 8 : 8;
            const dataAvailColIndex = comparisonMode ? 10 : 10;
            const varianceColIndex = comparisonMode ? 19 : 19;
            const fuelLossColIndex = comparisonMode ? 20 : 20;
            
            // Total IDLE - Red if percentage > 5%
            if (columnIndex === idleColIndex && metadata.idlePercentage > 5) {
              data.cell.styles.textColor = [255, 77, 79]; // #ff4d4f
            }
            
            // Data Availability - Red only if less than 95%, no color if >= 95%
            if (columnIndex === dataAvailColIndex) {
              if (metadata.dataAvailabilityPercentage < 95) {
                data.cell.styles.textColor = [255, 77, 79]; // #ff4d4f (red) - less than 95%
              }
              // No color (default/black) if >= 95%
            }
            
            // Variance - Use same logic as UI: Red if fuel loss, Green if fuel saved
            // Match UI render logic exactly from lines 3416-3450
            if (columnIndex === varianceColIndex) {
              const variance = metadata.consumptionVariance;
              // Only apply color if variance is not null/undefined (matches UI: "if (value === null || value === undefined) return 'N/A';")
              if (variance !== null && variance !== undefined) {
                // Ensure variance is a number
                const varianceNum = typeof variance === 'number' ? variance : parseFloat(variance);
                if (!isNaN(varianceNum)) {
                  let isFuelLoss = false;
                  
                  // Priority: use fuelLoss value if available (most accurate), otherwise use variance with unit logic
                  // This matches the UI logic exactly
                  if (metadata.fuelLoss !== null && metadata.fuelLoss !== undefined) {
                    const fuelLossNum = typeof metadata.fuelLoss === 'number' ? metadata.fuelLoss : parseFloat(metadata.fuelLoss);
                    if (!isNaN(fuelLossNum)) {
                      // Use fuelLoss value directly (positive = loss, negative = saved)
                      isFuelLoss = fuelLossNum > 0;
                    }
                  }
                  
                  // If fuelLoss not available or invalid, use variance with unit logic
                  if (metadata.fuelLoss === null || metadata.fuelLoss === undefined || isNaN(parseFloat(metadata.fuelLoss))) {
                    // Fallback: determine based on consumption rate unit and variance direction
                    const standardUnit = metadata.standardConsumptionRateUnit;
                    
                    if (standardUnit === 'KMS/Ltr') {
                      // For KMS/Ltr: Negative variance = lower rate = more fuel consumed = fuel loss
                      // Positive variance = higher rate = less fuel consumed = fuel saved
                      isFuelLoss = varianceNum < 0;
                    } else if (standardUnit === 'Ltr/Hrs') {
                      // For Ltr/Hrs: Positive variance = higher rate = more fuel consumed = fuel loss
                      // Negative variance = lower rate = less fuel consumed = fuel saved
                      isFuelLoss = varianceNum > 0;
                    } else {
                      // No unit info available - show variance as-is with neutral interpretation
                      // Default to green (saved) if no unit info (matches UI: isFuelLoss = false)
                      isFuelLoss = false;
                    }
                  }
                  
                  // Red for fuel loss (#ff4d4f), Green for fuel saved (#52c41a) - matches UI exactly
                  data.cell.styles.textColor = isFuelLoss ? [255, 77, 79] : [82, 196, 26];
                }
              }
            }
            
            // Fuel Loss - Simple rule: Positive = RED (loss), Negative = GREEN (saved), Zero = BLUE
            // This matches the UI table exactly: value > 0 = red, value < 0 = green, value === 0 = blue
            if (columnIndex === fuelLossColIndex) {
              const fuelLoss = metadata.fuelLoss;
              // Only apply color if fuelLoss is not null/undefined
              if (fuelLoss !== null && fuelLoss !== undefined) {
                // Ensure fuelLoss is a number - parse it to handle any string values
                const fuelLossNum = typeof fuelLoss === 'number' ? fuelLoss : parseFloat(String(fuelLoss));
                
                // Simple rule: positive = red, negative = green, zero = blue
                // No unit-based logic, just the raw value
                if (!isNaN(fuelLossNum)) {
                  if (fuelLossNum > 0) {
                    // Positive value = fuel loss = RED
                    data.cell.styles.textColor = [255, 77, 79]; // #ff4d4f (red)
                    data.cell.styles.fontStyle = 'bold';
                  } else if (fuelLossNum < 0) {
                    // Negative value = fuel saved = GREEN
                    data.cell.styles.textColor = [82, 196, 26]; // #52c41a (green)
                    data.cell.styles.fontStyle = 'bold';
                  } else {
                    // Zero value = no change = BLUE
                    data.cell.styles.textColor = [24, 144, 255]; // #1890ff (blue)
                  }
                }
              }
            }
          }
        },
        willDrawCell: function(data) {
          // Apply colors right before drawing the cell - more reliable than didParseCell
          // Skip non-body rows (header, footer)
          if (data.row.section !== 'body') return;
          
          // In jsPDF-autoTable, data.row.index is 0-based for body rows
          const rowIndex = data.row.index;
          if (rowIndex >= 0 && rowIndex < tableDataWithMetadata.length) {
            const metadata = tableDataWithMetadata[rowIndex].metadata;
            const columnIndex = data.column.index;
            const differenceColIndex = 15;
            const varianceColIndex = 19;
            const fuelLossColIndex = 20;
            
            // Difference (IoT - Manual) - Color percentage based on value
            // Red if < -5%, Green if > +5%
            if (columnIndex === differenceColIndex) {
              const diffPercentage = metadata.differencePercentage;
              if (diffPercentage !== null && diffPercentage !== undefined) {
                const diffPercentageNum = typeof diffPercentage === 'number' ? diffPercentage : parseFloat(String(diffPercentage));
                if (!isNaN(diffPercentageNum)) {
                  // Color the entire cell, but the percentage (second line) will be more visible
                  if (diffPercentageNum < -5) {
                    data.cell.styles.textColor = [255, 77, 79]; // #ff4d4f (red) - less than -5%
                  } else if (diffPercentageNum > 5) {
                    data.cell.styles.textColor = [82, 196, 26]; // #52c41a (green) - greater than +5%
                  }
                  // Default color for values between -5% and +5%
                }
              }
            }
            
            // Variance - Use fuelLoss to determine color
            if (columnIndex === varianceColIndex) {
              const fuelLoss = metadata.fuelLoss;
              if (fuelLoss !== null && fuelLoss !== undefined) {
                const fuelLossNum = typeof fuelLoss === 'number' ? fuelLoss : parseFloat(String(fuelLoss));
                if (!isNaN(fuelLossNum)) {
                  if (fuelLossNum > 0) {
                    data.cell.styles.textColor = [255, 77, 79]; // #ff4d4f (red) - loss
                  } else if (fuelLossNum < 0) {
                    data.cell.styles.textColor = [82, 196, 26]; // #52c41a (green) - saved
                  }
                }
              }
            }
            
            // Fuel Loss - Simple rule: Positive = RED (loss), Negative = GREEN (saved), Zero = BLUE
            if (columnIndex === fuelLossColIndex) {
              const fuelLoss = metadata.fuelLoss;
              if (fuelLoss !== null && fuelLoss !== undefined) {
                const fuelLossNum = typeof fuelLoss === 'number' ? fuelLoss : parseFloat(String(fuelLoss));
                
                if (!isNaN(fuelLossNum)) {
                  // Override any previous color settings
                  if (fuelLossNum > 0) {
                    data.cell.styles.textColor = [255, 77, 79]; // #ff4d4f (red) - positive = loss
                    data.cell.styles.fontStyle = 'bold';
                  } else if (fuelLossNum < 0) {
                    data.cell.styles.textColor = [82, 196, 26]; // #52c41a (green) - negative = saved
                    data.cell.styles.fontStyle = 'bold';
                  } else {
                    data.cell.styles.textColor = [24, 144, 255]; // #1890ff (blue) - zero = no change
                  }
                }
              }
            }
          }
        }
      });
      
      // Save PDF
      const fileName = `Sensor-Based_Fuel_Utilization_Report_${comparisonMode ? 'Comparison' : selectedMonth.format('YYYY-MM')}.pdf`;
      doc.save(fileName);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error stack:', error.stack);
      message.error(`Failed to generate PDF: ${error.message || error.toString()}`);
    }
  };

  const handleDownload = () => {
    // Prepare CSV data
    const headers = [
      'Sn No.',
      'Asset Code',
      'Asset Type',
      'Asset Sub Type',
      'Asset Model',
      'Owner Code',
      'Alt Name',
      'Total Ignition',
      'Total IDLE',
      'Total MOVING',
      'Data Availability',
      'Total Units',
      'Total FUEL CONSUMED',
      'Total FUEL ADDED',
      'Total Fuel Added (Manual Entry)',
      'Total Possible Leak',
      'Report Consumption Rate',
      'Standard Consumption Rate',
      'Variance (%)',
      'Fuel Loss (ltrs)'
    ];
    
    const csvRows = filteredData.map(item => [
      item.snNo,
      item.assetCode,
      item.assetType,
      item.assetSubType,
      item.assetModel,
      item.ownerCode,
      item.altName,
      item.totalIgnition.toFixed(2),
      item.totalIdle.toFixed(2),
      item.totalMoving.toFixed(2),
      `${item.totalDataAvailable.toFixed(2)} hrs (${((item.totalDataAvailable / (selectedMonth.daysInMonth() * 24)) * 100).toFixed(2)}%)`,
      item.totalUnits.toFixed(2),
      item.totalFuelConsumed.toFixed(2),
      item.totalFuelAdded.toFixed(2),
      (item.totalFuelAddedFromConsumptionReport || 0).toFixed(2),
      item.totalPossibleLeak.toFixed(2),
      item.consumptionRate > 0 ? `${item.consumptionRate.toFixed(2)} ${item.standardConsumptionRateUnit || (item.unitOfMeasurement === 'KMS' ? 'KMS/Ltr' : 'Hrs/Ltr')}` : '0.00',
      item.standardConsumptionRate ? `${item.standardConsumptionRate.toFixed(2)} ${item.standardConsumptionRateUnit || ''}` : 'N/A',
      item.consumptionVariance !== null && item.consumptionVariance !== undefined ? `${item.consumptionVariance.toFixed(2)}%` : 'N/A',
      item.fuelLoss !== null && item.fuelLoss !== undefined ? `${item.fuelLoss >= 0 ? '+' : ''}${item.fuelLoss.toFixed(2)}` : 'N/A',
      item.fullTankRefuelCount || 0,
      item.fullTankAvgConsumptionRate ? `${item.fullTankAvgConsumptionRate.toFixed(2)} ${item.fullTankAvgConsumptionRateUnit || ''}` : 'N/A'
    ]);
    
    // Convert to CSV
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Sensor-Based_Fuel_Utilization_Report_${selectedMonth.format('YYYY-MM')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('Report downloaded successfully');
  };

  const handleResetFilters = () => {
    setSelectedAssetCode(null);
    setSelectedAssetType(null);
    setSelectedAssetSubType(null);
    setSelectedAssetModel(null);
    setSelectedLocation(null);
    setSelectedSubLocation(null);
  };

  const handleViewDailyBreakdown = (record) => {
    setSelectedRecord(record);
    
    // Determine the month to filter by
    const monthForFilter = record.periodMoment || (record.periodKey ? moment(record.periodKey, 'YYYY-MM') : selectedMonth);
    const monthStart = moment(monthForFilter).startOf('month');
    const monthEnd = moment(monthForFilter).endOf('month');
    
    // Filter daily data for this asset and month
    const breakdown = dailyData.filter(row => {
      // Match asset code
      const rowAssetCode = getStringValue(row, 'Asset Code');
      if (rowAssetCode !== record.assetCode) return false;
      
      // Match date within month
      const dateStr = row['Date'] || row['"Date"'] || row['date'] || row['DATE'] || '';
      if (!dateStr) return false;
      
      const cleanDateStr = String(dateStr).replace(/^"|"$/g, '').trim();
      let date = moment(cleanDateStr, 'DD/MM/YYYY', true);
      if (!date.isValid()) {
        date = moment(cleanDateStr, 'DD-MM-YYYY', true);
      }
      if (!date.isValid()) {
        date = moment(cleanDateStr, 'DD/MM/YY', true);
      }
      
      if (!date.isValid()) return false;
      return date.isBetween(monthStart, monthEnd, 'day', '[]');
    });
    
    // Sort by date
    breakdown.sort((a, b) => {
      const dateA = getDateFromRow(a);
      const dateB = getDateFromRow(b);
      if (!dateA || !dateB) return 0;
      return dateA.valueOf() - dateB.valueOf();
    });
    
    setDailyBreakdownData(breakdown);
    setDailyBreakdownVisible(true);
  };

  const getDateFromRow = (row) => {
    const dateStr = row['Date'] || row['"Date"'] || row['date'] || row['DATE'] || '';
    if (!dateStr) return null;
    
    const cleanDateStr = String(dateStr).replace(/^"|"$/g, '').trim();
    let date = moment(cleanDateStr, 'DD/MM/YYYY', true);
    if (!date.isValid()) {
      date = moment(cleanDateStr, 'DD-MM-YYYY', true);
    }
    if (!date.isValid()) {
      date = moment(cleanDateStr, 'DD/MM/YY', true);
    }
    return date.isValid() ? date : null;
  };

  const handleViewFullTankBreakdown = (record) => {
    setSelectedRecord(record);
    
    // Determine the month to filter by
    const monthForFilter = record.periodMoment || (record.periodKey ? moment(record.periodKey, 'YYYY-MM') : selectedMonth);
    const monthKey = monthForFilter.format('YYYY-MM');
    const monthStart = moment(monthForFilter).startOf('month');
    const monthEnd = moment(monthForFilter).endOf('month');
    
    // Filter all fuel entries (both full tank and non-full tank) for this asset and month
    const breakdown = fullTankRawData.filter(row => {
      // Match asset code
      const rowAssetCode = (row['Asset'] || '').trim();
      if (rowAssetCode !== record.assetCode) return false;
      
      // Include both full tank and non-full tank events (no filter on Full Tank field)
      
      // Match date within month
      const dateStr = (row['Date'] || '').trim();
      if (!dateStr) return false;
      
      const cleanDateStr = dateStr.replace(/^["']|["']$/g, '');
      const datePart = cleanDateStr.split('_')[0]; // Get date part before underscore
      const date = moment(datePart, 'DD-MM-YYYY', true);
      
      if (!date.isValid()) return false;
      return date.isBetween(monthStart, monthEnd, 'day', '[]');
    });
    
    // Sort by date
    breakdown.sort((a, b) => {
      const dateA = getDateFromFullTankRow(a);
      const dateB = getDateFromFullTankRow(b);
      if (!dateA || !dateB) return 0;
      return dateA.valueOf() - dateB.valueOf();
    });
    
    setFullTankBreakdownData(breakdown);
    setFullTankBreakdownVisible(true);
  };

  const getDateFromFullTankRow = (row) => {
    const dateStr = (row['Date'] || '').trim();
    if (!dateStr) return null;
    
    const cleanDateStr = dateStr.replace(/^["']|["']$/g, '');
    const datePart = cleanDateStr.split('_')[0]; // Get date part before underscore
    const date = moment(datePart, 'DD-MM-YYYY', true);
    return date.isValid() ? date : null;
  };

  const columns = [
    {
      title: 'Sn No.',
      dataIndex: 'snNo',
      key: 'snNo',
      width: 80,
      fixed: 'left',
      sorter: (a, b) => a.snNo - b.snNo
    },
    {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      width: 120,
      fixed: 'left',
      sorter: (a, b) => (a.assetCode || '').localeCompare(b.assetCode || '')
    },
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 150,
      sorter: (a, b) => (a.assetType || '').localeCompare(b.assetType || '')
    },
    {
      title: 'Asset Sub Type',
      dataIndex: 'assetSubType',
      key: 'assetSubType',
      width: 180,
      sorter: (a, b) => (a.assetSubType || '').localeCompare(b.assetSubType || '')
    },
    {
      title: 'Asset Model',
      dataIndex: 'assetModel',
      key: 'assetModel',
      width: 200,
      sorter: (a, b) => (a.assetModel || '').localeCompare(b.assetModel || '')
    },
    {
      title: 'Owner Code',
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      width: 120,
      sorter: (a, b) => (a.ownerCode || '').localeCompare(b.ownerCode || '')
    },
    {
      title: 'Alt Name',
      dataIndex: 'altName',
      key: 'altName',
      width: 120,
      sorter: (a, b) => (a.altName || '').localeCompare(b.altName || '')
    },
    {
      title: 'Total Ignition',
      dataIndex: 'totalIgnition',
      key: 'totalIgnition',
      width: 130,
      sorter: (a, b) => a.totalIgnition - b.totalIgnition,
      render: (value) => `${value.toFixed(2)} min`
    },
    {
      title: 'Total IDLE',
      dataIndex: 'totalIdle',
      key: 'totalIdle',
      width: 150,
      sorter: (a, b) => a.totalIdle - b.totalIdle,
      render: (value, record) => {
        const percentage = record.totalIgnition > 0 
          ? ((value / record.totalIgnition) * 100).toFixed(2) 
          : '0.00';
        const percentageValue = parseFloat(percentage);
        const color = percentageValue > 5 ? '#ff4d4f' : '#666';
        return (
          <div>
            <div>{value.toFixed(2)} min</div>
            <div style={{ fontSize: '12px', color: color, marginTop: '2px', fontWeight: percentageValue > 5 ? 'bold' : 'normal' }}>
              ({percentage}%)
            </div>
          </div>
        );
      }
    },
    {
      title: 'Total MOVING',
      dataIndex: 'totalMoving',
      key: 'totalMoving',
      width: 150,
      sorter: (a, b) => a.totalMoving - b.totalMoving,
      render: (value, record) => {
        const percentage = record.totalIgnition > 0 
          ? ((value / record.totalIgnition) * 100).toFixed(2) 
          : '0.00';
        return (
          <div>
            <div>{value.toFixed(2)} min</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              ({percentage}%)
            </div>
          </div>
        );
      }
    },
    {
      title: 'Data Availability',
      dataIndex: 'dataAvailability',
      key: 'dataAvailability',
      width: 150,
      sorter: (a, b) => a.totalDataAvailable - b.totalDataAvailable,
      render: (value, record) => {
        // Calculate percentage: (Total Data Available / (Days in month × 24)) × 100
        // Use the month from the record if available (comparison mode), otherwise use selectedMonth
        const monthForCalc = record.periodMoment || (record.periodKey ? moment(record.periodKey, 'YYYY-MM') : selectedMonth);
        const daysInMonth = monthForCalc.daysInMonth();
        const totalPossibleHours = daysInMonth * 24;
        const percentage = totalPossibleHours > 0 
          ? ((record.totalDataAvailable / totalPossibleHours) * 100).toFixed(2) 
          : '0.00';
        const percentageValue = parseFloat(percentage);
        const color = percentageValue < 90 ? '#ff4d4f' : percentageValue < 95 ? '#faad14' : '#52c41a';
        return (
          <div>
            <div>{record.totalDataAvailable.toFixed(2)} hrs</div>
            <div style={{ fontSize: '12px', color: color, marginTop: '2px', fontWeight: percentageValue < 90 ? 'bold' : 'normal' }}>
              ({percentage}%)
            </div>
          </div>
        );
      }
    },
    {
      title: 'Total Units',
      dataIndex: 'totalUnits',
      key: 'totalUnits',
      width: 120,
      sorter: (a, b) => a.totalUnits - b.totalUnits,
      render: (value, record) => `${value.toFixed(2)} ${record.unitOfMeasurement}`
    },
    {
      title: 'Total FUEL CONSUMED',
      dataIndex: 'totalFuelConsumed',
      key: 'totalFuelConsumed',
      width: 160,
      sorter: (a, b) => a.totalFuelConsumed - b.totalFuelConsumed,
      render: (value) => `${value.toFixed(2)} ltrs`
    },
    {
      title: 'Total FUEL ADDED',
      dataIndex: 'totalFuelAdded',
      key: 'totalFuelAdded',
      width: 150,
      sorter: (a, b) => a.totalFuelAdded - b.totalFuelAdded,
      render: (value) => `${value.toFixed(2)} ltrs`
    },
    {
      title: 'Total Fuel Added (Manual Entry)',
      dataIndex: 'totalFuelAddedFromConsumptionReport',
      key: 'totalFuelAddedFromConsumptionReport',
      width: 200,
      sorter: (a, b) => (a.totalFuelAddedFromConsumptionReport || 0) - (b.totalFuelAddedFromConsumptionReport || 0),
      render: (value) => {
        if (value === null || value === undefined) return '0.00 ltrs';
        return `${value.toFixed(2)} ltrs`;
      }
    },
    {
      title: 'Difference (IoT - Manual)',
      key: 'fuelAddedDifference',
      width: 200,
      sorter: (a, b) => {
        const diffA = (a.totalFuelAdded || 0) - (a.totalFuelAddedFromConsumptionReport || 0);
        const diffB = (b.totalFuelAdded || 0) - (b.totalFuelAddedFromConsumptionReport || 0);
        return diffA - diffB;
      },
      render: (value, record) => {
        const totalFuelAdded = record.totalFuelAdded || 0;
        const manualEntry = record.totalFuelAddedFromConsumptionReport || 0;
        const difference = totalFuelAdded - manualEntry;
        const percentage = manualEntry > 0 ? ((difference / manualEntry) * 100) : (difference !== 0 ? (difference > 0 ? 100 : -100) : 0);
        
        const color = Math.abs(percentage) > 10 ? '#ff4d4f' : Math.abs(percentage) > 5 ? '#faad14' : '#52c41a';
        
        return (
          <div>
            <div>{difference.toFixed(2)} ltrs</div>
            <div style={{ fontSize: '12px', color: color, marginTop: '2px', fontWeight: Math.abs(percentage) > 10 ? 'bold' : 'normal' }}>
              ({percentage >= 0 ? '+' : ''}{percentage.toFixed(2)}%)
            </div>
          </div>
        );
      }
    },
    {
      title: 'Total Possible Leak',
      dataIndex: 'totalPossibleLeak',
      key: 'totalPossibleLeak',
      width: 150,
      sorter: (a, b) => a.totalPossibleLeak - b.totalPossibleLeak,
      render: (value) => `${value.toFixed(2)} ltrs`
    },
    {
      title: 'Report Consumption Rate',
      dataIndex: 'consumptionRate',
      key: 'consumptionRate',
      width: 160,
      sorter: (a, b) => (a.consumptionRate || 0) - (b.consumptionRate || 0),
      render: (value, record) => {
        if (value === 0) return '0.00';
        // Use standard rate unit if available, otherwise determine from unit of measurement
        let unit;
        if (record.standardConsumptionRateUnit) {
          unit = record.standardConsumptionRateUnit;
        } else {
          unit = record.unitOfMeasurement === 'KMS' ? 'KMS/Ltr' : 'Hrs/Ltr';
        }
        return (
          <span 
            style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
            onClick={() => handleViewDailyBreakdown(record)}
            title="Click to view daily breakdown"
          >
            {value.toFixed(2)} {unit}
          </span>
        );
      }
    },
    {
      title: 'Standard Consumption Rate',
      dataIndex: 'standardConsumptionRate',
      key: 'standardConsumptionRate',
      width: 180,
      sorter: (a, b) => {
        const aVal = a.standardConsumptionRate || 0;
        const bVal = b.standardConsumptionRate || 0;
        return aVal - bVal;
      },
      render: (value, record) => {
        if (!value) return 'N/A';
        return `${value.toFixed(2)} ${record.standardConsumptionRateUnit || ''}`;
      }
    },
    {
      title: 'Variance',
      dataIndex: 'consumptionVariance',
      key: 'consumptionVariance',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.consumptionVariance !== null && a.consumptionVariance !== undefined ? a.consumptionVariance : 0;
        const bVal = b.consumptionVariance !== null && b.consumptionVariance !== undefined ? b.consumptionVariance : 0;
        return aVal - bVal;
      },
      render: (value, record) => {
        if (value === null || value === undefined) return 'N/A';
        
        // Determine if variance indicates fuel loss or fuel saved
        // Priority: use fuelLoss value if available (most accurate), otherwise use variance with unit logic
        let isFuelLoss = false;
        let sign = '';
        
        if (record.fuelLoss !== null && record.fuelLoss !== undefined) {
          // Use fuelLoss value directly (positive = loss, negative = saved)
          isFuelLoss = record.fuelLoss > 0;
          sign = isFuelLoss ? '-' : '+';
        } else {
          // Fallback: determine based on consumption rate unit and variance direction
          const standardUnit = record.standardConsumptionRateUnit;
          
          if (standardUnit === 'KMS/Ltr') {
            // For KMS/Ltr: Negative variance = lower rate = more fuel consumed = fuel loss
            // Positive variance = higher rate = less fuel consumed = fuel saved
            isFuelLoss = value < 0;
            sign = isFuelLoss ? '-' : '+';
          } else if (standardUnit === 'Ltr/Hrs') {
            // For Ltr/Hrs: Positive variance = higher rate = more fuel consumed = fuel loss
            // Negative variance = lower rate = less fuel consumed = fuel saved
            isFuelLoss = value > 0;
            sign = isFuelLoss ? '-' : '+';
          } else {
            // No unit info available - show variance as-is with neutral interpretation
            sign = value >= 0 ? '+' : '-';
            isFuelLoss = false; // Neutral color
          }
        }
        
        const color = isFuelLoss ? '#ff4d4f' : '#52c41a';
        return <span style={{ color }}>{sign}{Math.abs(value).toFixed(2)}%</span>;
      }
    },
    {
      title: 'Fuel Loss',
      dataIndex: 'fuelLoss',
      key: 'fuelLoss',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.fuelLoss !== null && a.fuelLoss !== undefined ? a.fuelLoss : 0;
        const bVal = b.fuelLoss !== null && b.fuelLoss !== undefined ? b.fuelLoss : 0;
        return aVal - bVal;
      },
      render: (value, record) => {
        if (value === null || value === undefined) return 'N/A';
        // Positive = fuel loss (bad), Negative = fuel saved (good)
        // For KMS/Ltr: lower rate = loss, higher rate = saved
        // For Ltr/Hrs: higher rate = loss, lower rate = saved
        const color = value > 0 ? '#ff4d4f' : value < 0 ? '#52c41a' : '#1890ff';
        const sign = value >= 0 ? '+' : '';
        const label = value > 0 ? 'Loss' : value < 0 ? 'Saved' : 'No Change';
        return <span style={{ color, fontWeight: value !== 0 ? 'bold' : 'normal' }}>
          {sign}{value.toFixed(2)} ltrs ({label})
        </span>;
      }
    },
    {
      title: 'Full Tank Refuel Count',
      dataIndex: 'fullTankRefuelCount',
      key: 'fullTankRefuelCount',
      width: 150,
      sorter: (a, b) => (a.fullTankRefuelCount || 0) - (b.fullTankRefuelCount || 0),
      render: (value, record) => {
        // Show loading spinner if full tank data is still loading
        // Check if we're in a loading state OR if data hasn't been loaded yet
        if (loading || !fullTankDataLoaded) {
          return <Spin size="small" />;
        }
        
        // Once data is loaded, show the actual value (0 means no full tank events)
        return value > 0 ? value : '0';
      }
    },
    {
      title: 'Full Tank Avg Consumption Rate',
      dataIndex: 'fullTankAvgConsumptionRate',
      key: 'fullTankAvgConsumptionRate',
      width: 200,
      sorter: (a, b) => {
        const aVal = a.fullTankAvgConsumptionRate || 0;
        const bVal = b.fullTankAvgConsumptionRate || 0;
        return aVal - bVal;
      },
      render: (value, record) => {
        // Show loading spinner if full tank data is still loading
        if (loading || !fullTankDataLoaded) {
          return <Spin size="small" />;
        }
        
        // Once data is loaded, show the actual value (N/A means no full tank events or no rates)
        if (!value || value === null || value === undefined) return 'N/A';
        return (
          <span 
            style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
            onClick={() => handleViewFullTankBreakdown(record)}
            title="Click to view full tank refueling events"
          >
            {value.toFixed(2)} {record.fullTankAvgConsumptionRateUnit || ''}
          </span>
        );
      }
    }
  ];

  // Calculate totals for summary
  const totals = filteredData.reduce((acc, item) => {
    acc.totalIgnition += item.totalIgnition;
    acc.totalIdle += item.totalIdle;
    acc.totalMoving += item.totalMoving;
    acc.totalDataAvailable += item.totalDataAvailable;
    acc.totalUnits += item.totalUnits;
    acc.totalFuelConsumed += item.totalFuelConsumed;
    acc.totalFuelAdded += item.totalFuelAdded;
    acc.totalPossibleLeak += item.totalPossibleLeak;
    acc.totalFuelAddedManualEntry += (item.totalFuelAddedFromConsumptionReport || item.totalManualRefuelQuantity || 0);
    // Sum fuel loss (positive = loss, negative = saved)
    if (item.fuelLoss !== null && item.fuelLoss !== undefined) {
      acc.totalFuelLoss += item.fuelLoss;
    }
    return acc;
  }, {
    totalIgnition: 0,
    totalIdle: 0,
    totalMoving: 0,
    totalDataAvailable: 0,
    totalUnits: 0,
    totalFuelConsumed: 0,
    totalFuelAdded: 0,
    totalPossibleLeak: 0,
    totalFuelAddedManualEntry: 0,
    totalFuelLoss: 0
  });

  // Calculate difference and percentage for summary
  const fuelAddedDifference = totals.totalFuelAdded - totals.totalFuelAddedManualEntry;
  const fuelAddedDifferencePercentage = totals.totalFuelAddedManualEntry > 0 
    ? ((fuelAddedDifference / totals.totalFuelAddedManualEntry) * 100)
    : (fuelAddedDifference !== 0 ? (fuelAddedDifference > 0 ? 100 : -100) : 0);

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card
        title={
          <span>
            <Icon type="filter" style={{ marginRight: 8 }} />
            Sensor-Based Fuel & Utilization Report {comparisonMode ? '- Comparison' : `- ${selectedMonth.format('MMMM YYYY')}`}
          </span>
        }
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={comparisonMode}
              onChange={setComparisonMode}
              checkedChildren="Compare"
              unCheckedChildren="Single"
              style={{ marginRight: 16 }}
            />
            {!comparisonMode ? (
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={(date) => {
                  if (date) {
                    setSelectedMonth(date);
                  }
                }}
                format="MMMM YYYY"
                style={{ marginRight: 8 }}
              />
            ) : (
              <Select
                mode="multiple"
                placeholder="Select periods to compare"
                value={selectedPeriods.map(p => p.format('YYYY-MM'))}
                onChange={(values) => {
                  setSelectedPeriods(values.map(v => moment(v, 'YYYY-MM')));
                }}
                style={{ width: 300, marginRight: 8 }}
                allowClear
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const month = moment().subtract(i, 'months');
                  return (
                    <Option key={month.format('YYYY-MM')} value={month.format('YYYY-MM')}>
                      {month.format('MMM YYYY')}
                    </Option>
                  );
                })}
              </Select>
            )}
            <Button
              icon="reload"
              onClick={() => comparisonMode ? loadComparisonData() : loadSummaryData()}
              style={{ marginRight: 8 }}
            >
              Reload
            </Button>
            <Button
              icon="download"
              onClick={handleDownload}
              style={{ marginRight: 8 }}
            >
              Download CSV
            </Button>
            <Button
              type="primary"
              icon="file-pdf"
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>
          </div>
        }
      >
        {/* Filters */}
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Filters</span>
              <Button
                type="link"
                size="small"
                icon={filtersVisible ? "up" : "down"}
                onClick={() => setFiltersVisible(!filtersVisible)}
                style={{ padding: 0, height: 'auto' }}
              >
                {filtersVisible ? 'Hide' : 'Show'} Filters
              </Button>
            </div>
          }
        >
          {filtersVisible && (
            <>
          <Row gutter={16}>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Asset Code:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="All Asset Codes (search by Code, Owner Code, or Alt Name)"
                allowClear
                value={selectedAssetCode}
                onChange={setSelectedAssetCode}
                showSearch
                filterOption={(input, option) => {
                  // If no input, show all options
                  if (!input || String(input).trim() === '') {
                    return true;
                  }
                  
                  const searchText = String(input).toLowerCase().trim();
                  
                  // Get the display text from the option (this contains code, owner code, and alt name)
                  const displayText = option.children || option.props?.children || '';
                  const displayTextLower = String(displayText).toLowerCase();
                  
                  // Also get the code value for direct matching
                  const code = option.value || option.props?.value || '';
                  const codeLower = String(code).toLowerCase();
                  
                  // Get asset info from map for additional search fields
                  const assetInfo = assetCodeMap[code] || {};
                  const ownerCode = String(assetInfo.ownerCode || '').toLowerCase();
                  const altName = String(assetInfo.altName || '').toLowerCase();
                  
                  // Search across all fields
                  return displayTextLower.includes(searchText) ||
                         codeLower.includes(searchText) ||
                         ownerCode.includes(searchText) ||
                         altName.includes(searchText);
                }}
              >
                {assetCodes.map(code => {
                  const assetInfo = assetCodeMap[code];
                  const displayText = assetInfo 
                    ? `${code}${assetInfo.ownerCode ? ` (${assetInfo.ownerCode})` : ''}${assetInfo.altName ? ` - ${assetInfo.altName}` : ''}`
                    : code;
                  return (
                    <Option key={code} value={code}>{displayText}</Option>
                  );
                })}
              </Select>
            </Col>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Asset Type:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="All Asset Types"
                allowClear
                value={selectedAssetType}
                onChange={setSelectedAssetType}
              >
                {assetTypes.map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Asset Sub Type:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="All Asset Sub Types"
                allowClear
                value={selectedAssetSubType}
                onChange={(value) => {
                  setSelectedAssetSubType(value);
                  setSelectedAssetModel(null); // Reset model when sub type changes
                }}
              >
                {assetSubTypes.map(subType => (
                  <Option key={subType} value={subType}>{subType}</Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Asset Model:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="All Asset Models"
                allowClear
                value={selectedAssetModel}
                onChange={setSelectedAssetModel}
              >
                {assetModels.map(model => (
                  <Option key={model} value={model}>{model}</Option>
                ))}
              </Select>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Location:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="All Locations"
                allowClear
                value={selectedLocation}
                onChange={setSelectedLocation}
              >
                {locations.map(location => (
                  <Option key={location} value={location}>{location}</Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Sub Location:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="All Sub Locations"
                allowClear
                value={selectedSubLocation}
                onChange={setSelectedSubLocation}
              >
                {subLocations.map(subLocation => (
                  <Option key={subLocation} value={subLocation}>{subLocation}</Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <Button
                onClick={handleResetFilters}
                style={{ marginTop: 30 }}
              >
                Reset Filters
              </Button>
            </Col>
          </Row>
            </>
          )}
        </Card>

        {/* Summary Statistics */}
        {!comparisonMode && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8} lg={4} xl={4}>
              <Card>
                <Statistic
                  title="Total Assets"
                  value={filteredData.length}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
              <Card>
                <Statistic
                  title="Total Fuel Consumed"
                  value={totals.totalFuelConsumed.toFixed(2)}
                  suffix="ltrs"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
              <Card>
                <Statistic
                  title="Total Fuel Added"
                  value={totals.totalFuelAdded.toFixed(2)}
                  suffix="ltrs"
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
              <Card>
                <Statistic
                  title="Total Fuel Added Manual Entry"
                  value={totals.totalFuelAddedManualEntry.toFixed(2)}
                  suffix="ltrs"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
              <Card>
                <Statistic
                  title="Difference (IoT - Manual)"
                  value={fuelAddedDifference.toFixed(2)}
                  suffix="ltrs"
                  valueStyle={{ 
                    color: Math.abs(fuelAddedDifferencePercentage) > 10 ? '#ff4d4f' : Math.abs(fuelAddedDifferencePercentage) > 5 ? '#faad14' : '#52c41a',
                    fontWeight: Math.abs(fuelAddedDifferencePercentage) > 10 ? 'bold' : 'normal'
                  }}
                  prefix={fuelAddedDifference >= 0 ? '+' : ''}
                />
                <div style={{ 
                  marginTop: 8, 
                  fontSize: '14px', 
                  color: Math.abs(fuelAddedDifferencePercentage) > 10 ? '#ff4d4f' : Math.abs(fuelAddedDifferencePercentage) > 5 ? '#faad14' : '#52c41a',
                  fontWeight: Math.abs(fuelAddedDifferencePercentage) > 10 ? 'bold' : 'normal'
                }}>
                  ({fuelAddedDifferencePercentage >= 0 ? '+' : ''}{fuelAddedDifferencePercentage.toFixed(2)}%)
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
              <Card>
                <Statistic
                  title="Total Possible Leak"
                  value={totals.totalPossibleLeak.toFixed(2)}
                  suffix="ltrs"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
              <Card>
                <Statistic
                  title={totals.totalFuelLoss >= 0 ? "Total Fuel Lost" : "Total Fuel Saved"}
                  value={Math.abs(totals.totalFuelLoss).toFixed(2)}
                  suffix="ltrs"
                  valueStyle={{ 
                    color: totals.totalFuelLoss >= 0 ? '#ff4d4f' : '#52c41a',
                    fontWeight: 'bold'
                  }}
                  prefix={totals.totalFuelLoss >= 0 ? '+' : '-'}
                />
              </Card>
            </Col>
          </Row>
        )}
        {comparisonMode && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
                  {selectedPeriods.map((period, index) => {
                    const periodKey = period.format('YYYY-MM');
                    const periodLabel = period.format('MMM YYYY');
                    const periodData = comparisonData.reduce((acc, asset) => {
                      const data = asset.periods[periodKey];
                      if (data) {
                        acc.fuelConsumed += data.totalFuelConsumed || 0;
                        acc.fuelLoss += data.fuelLoss || 0;
                        acc.assets += 1;
                      }
                      return acc;
                    }, { fuelConsumed: 0, fuelLoss: 0, assets: 0 });
                    
                    return (
                      <div key={periodKey} style={{ textAlign: 'center', margin: '0 16px', minWidth: 200 }}>
                        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px', marginBottom: 8 }}>
                          {periodLabel}
                        </Tag>
                        <div>
                          <Statistic
                            title="Assets"
                            value={periodData.assets}
                            valueStyle={{ fontSize: '16px' }}
                          />
                          <Statistic
                            title="Fuel Consumed"
                            value={periodData.fuelConsumed.toFixed(2)}
                            suffix="ltrs"
                            valueStyle={{ fontSize: '16px', color: '#52c41a' }}
                          />
                          <Statistic
                            title={periodData.fuelLoss >= 0 ? "Fuel Lost" : "Fuel Saved"}
                            value={Math.abs(periodData.fuelLoss).toFixed(2)}
                            suffix="ltrs"
                            valueStyle={{ 
                              fontSize: '16px',
                              color: periodData.fuelLoss >= 0 ? '#ff4d4f' : '#52c41a',
                              fontWeight: 'bold'
                            }}
                            prefix={periodData.fuelLoss >= 0 ? '+' : '-'}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Data Table */}
        {comparisonMode ? (
          <ComparisonTable 
            data={comparisonData}
            selectedPeriods={selectedPeriods}
            loading={loading}
            filters={{
              assetCode: selectedAssetCode,
              assetType: selectedAssetType,
              assetSubType: selectedAssetSubType,
              assetModel: selectedAssetModel,
              location: selectedLocation,
              subLocation: selectedSubLocation
            }}
            columns={columns}
            selectedMonth={selectedMonth}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record, index) => `${record.assetCode}_${index}`}
            loading={loading}
            scroll={{ x: 2000, y: 600 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} assets`
            }}
          />
        )}
      </Card>

      {/* Daily Breakdown Modal */}
      <Modal
        title={
          <span>
            <Icon type="calendar" style={{ marginRight: 8 }} />
            Daily Breakdown - {selectedRecord ? `${selectedRecord.assetCode} (${selectedRecord.altName || 'N/A'})` : ''}
          </span>
        }
        visible={dailyBreakdownVisible}
        onCancel={() => setDailyBreakdownVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDailyBreakdownVisible(false)}>
            Close
          </Button>
        ]}
        width={1400}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <strong>Asset Code:</strong> {selectedRecord.assetCode}
                </Col>
                <Col span={6}>
                  <strong>Asset Type:</strong> {selectedRecord.assetType}
                </Col>
                <Col span={6}>
                  <strong>Asset Sub Type:</strong> {selectedRecord.assetSubType}
                </Col>
                <Col span={6}>
                  <strong>Asset Model:</strong> {selectedRecord.assetModel}
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={6}>
                  <strong>Period:</strong> {selectedRecord.periodLabel || selectedMonth.format('MMMM YYYY')}
                </Col>
                <Col span={6}>
                  <strong>Total Days:</strong> {dailyBreakdownData.length}
                </Col>
                <Col span={6}>
                  <strong>Total Ignition:</strong> {selectedRecord.totalIgnition.toFixed(2)} min
                </Col>
                <Col span={6}>
                  <strong>Total Fuel:</strong> {selectedRecord.totalFuelConsumed.toFixed(2)} ltrs
                </Col>
              </Row>
            </div>
            <Table
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'date',
                  key: 'date',
                  width: 120,
                  fixed: 'left',
                  render: (text, record) => {
                    const date = getDateFromRow(record);
                    return date ? date.format('DD/MM/YYYY') : text || 'N/A';
                  },
                  sorter: (a, b) => {
                    const dateA = getDateFromRow(a);
                    const dateB = getDateFromRow(b);
                    if (!dateA || !dateB) return 0;
                    return dateA.valueOf() - dateB.valueOf();
                  }
                },
                {
                  title: 'Ignition Time (min)',
                  key: 'ignition',
                  width: 150,
                  render: (text, record) => getNumericValue(record, 'Ignition Time (min)').toFixed(2)
                },
                {
                  title: 'Idle Time (min)',
                  key: 'idle',
                  width: 130,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Idle Time (min)');
                    const ignition = getNumericValue(record, 'Ignition Time (min)');
                    const percentage = ignition > 0 ? ((value / ignition) * 100).toFixed(2) : '0.00';
                    const percentageValue = parseFloat(percentage);
                    const color = percentageValue > 5 ? '#ff4d4f' : '#666';
                    return (
                      <div>
                        <div>{value.toFixed(2)}</div>
                        <div style={{ fontSize: '11px', color: color }}>
                          ({percentage}%)
                        </div>
                      </div>
                    );
                  }
                },
                {
                  title: 'Moving Time (min)',
                  key: 'moving',
                  width: 140,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Moving Time (min)');
                    const ignition = getNumericValue(record, 'Ignition Time (min)');
                    const percentage = ignition > 0 ? ((value / ignition) * 100).toFixed(2) : '0.00';
                    return (
                      <div>
                        <div>{value.toFixed(2)}</div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          ({percentage}%)
                        </div>
                      </div>
                    );
                  }
                },
                {
                  title: 'Data Available (hours)',
                  key: 'dataAvailable',
                  width: 160,
                  render: (text, record) => getNumericValue(record, 'Data Available (hours)').toFixed(2)
                },
                {
                  title: 'Distance Travelled (km)',
                  key: 'distance',
                  width: 170,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Distance Travelled (km)');
                    return value > 0 ? value.toFixed(2) : '-';
                  }
                },
                {
                  title: 'Average Speed (KMPH)',
                  key: 'averageSpeed',
                  width: 160,
                  render: (text, record) => {
                    const distance = getNumericValue(record, 'Distance Travelled (km)');
                    const ignition = getNumericValue(record, 'Ignition Time (min)');
                    
                    if (distance > 0 && ignition > 0) {
                      // Average Speed = (Distance Travelled / Ignition Time) × 60
                      const averageSpeed = (distance / ignition) * 60;
                      return averageSpeed.toFixed(2);
                    }
                    return '-';
                  }
                },
                {
                  title: 'Fuel Consumed (ltrs)',
                  key: 'fuelConsumed',
                  width: 160,
                  render: (text, record) => getNumericValue(record, 'Fuel Consumed (ltrs)').toFixed(2)
                },
                {
                  title: 'Possible Refuel (ltrs)',
                  key: 'refuel',
                  width: 160,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Possible Refuel');
                    return value > 0 ? value.toFixed(2) : '-';
                  }
                },
                {
                  title: 'Manual Refuel Quantity (ltrs)',
                  key: 'manualRefuel',
                  width: 180,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Manual Refuel Quantity');
                    return value > 0 ? value.toFixed(2) : '-';
                  }
                },
                {
                  title: 'Difference (IoT - Manual)',
                  key: 'fuelDifference',
                  width: 200,
                  render: (text, record) => {
                    const possibleRefuel = getNumericValue(record, 'Possible Refuel');
                    const manualRefuel = getNumericValue(record, 'Manual Refuel Quantity');
                    const difference = possibleRefuel - manualRefuel;
                    const percentage = manualRefuel > 0 ? ((difference / manualRefuel) * 100) : (difference !== 0 ? (difference > 0 ? 100 : -100) : 0);
                    
                    if (possibleRefuel === 0 && manualRefuel === 0) return '-';
                    
                    const color = Math.abs(percentage) > 10 ? '#ff4d4f' : Math.abs(percentage) > 5 ? '#faad14' : '#52c41a';
                    
                    return (
                      <div>
                        <div>{difference.toFixed(2)} ltrs</div>
                        <div style={{ fontSize: '11px', color: color, marginTop: '2px', fontWeight: Math.abs(percentage) > 10 ? 'bold' : 'normal' }}>
                          ({percentage >= 0 ? '+' : ''}{percentage.toFixed(2)}%)
                        </div>
                      </div>
                    );
                  }
                },
                {
                  title: 'Possible Drain (ltrs)',
                  key: 'drain',
                  width: 150,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Possible Drain');
                    return value > 0 ? value.toFixed(2) : '-';
                  }
                },
                {
                  title: 'Consumption Rate',
                  key: 'consumptionRate',
                  width: 150,
                  render: (text, record) => {
                    const fuel = getNumericValue(record, 'Fuel Consumed (ltrs)');
                    const distance = getNumericValue(record, 'Distance Travelled (km)');
                    const ignition = getNumericValue(record, 'Ignition Time (min)');
                    
                    if (fuel > 0 && selectedRecord) {
                      // Use the same logic as summary report
                      const unitOfMeasurement = selectedRecord.unitOfMeasurement;
                      const standardConsumptionRateUnit = selectedRecord.standardConsumptionRateUnit;
                      
                      if (unitOfMeasurement === 'KMS' && distance > 0) {
                        // For KMS: calculate as KMS per Liter (to match KMS/Ltr standard)
                        const rate = distance / fuel;
                        return `${rate.toFixed(2)} KMS/Ltr`;
                      } else if (unitOfMeasurement === 'HOURS' && ignition > 0) {
                        // For HOURS: calculate based on standard rate unit
                        const hours = ignition / 60;
                        if (standardConsumptionRateUnit === 'Ltr/Hrs') {
                          // Standard is Ltr/Hrs, so calculate actual as Ltr/Hrs
                          const rate = fuel / hours;
                          return `${rate.toFixed(2)} Ltr/Hrs`;
                        } else {
                          // No standard or standard is in different unit, calculate as Hrs/Ltr
                          const rate = hours / fuel;
                          return `${rate.toFixed(2)} Hrs/Ltr`;
                        }
                      }
                    }
                    return '-';
                  }
                }
              ]}
              dataSource={dailyBreakdownData}
              rowKey={(record, index) => `daily_${index}_${getDateFromRow(record)?.format('YYYY-MM-DD') || index}`}
              scroll={{ x: 1200, y: 500 }}
              pagination={{
                pageSize: 31,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} days`
              }}
              size="small"
            />
          </div>
        )}
      </Modal>

      {/* Full Tank Breakdown Modal */}
      <Modal
        title={
          <span>
            <Icon type="fire" style={{ marginRight: 8 }} />
            Fuel Entries (Full Tank to Full Tank) - {selectedRecord ? `${selectedRecord.assetCode} (${selectedRecord.altName || 'N/A'})` : ''}
          </span>
        }
        visible={fullTankBreakdownVisible}
        onCancel={() => setFullTankBreakdownVisible(false)}
        footer={[
          <Button key="close" onClick={() => setFullTankBreakdownVisible(false)}>
            Close
          </Button>
        ]}
        width={1400}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <strong>Asset Code:</strong> {selectedRecord.assetCode}
                </Col>
                <Col span={6}>
                  <strong>Asset Type:</strong> {selectedRecord.assetType}
                </Col>
                <Col span={6}>
                  <strong>Asset Sub Type:</strong> {selectedRecord.assetSubType}
                </Col>
                <Col span={6}>
                  <strong>Asset Model:</strong> {selectedRecord.assetModel}
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={6}>
                  <strong>Period:</strong> {selectedRecord.periodLabel || selectedMonth.format('MMMM YYYY')}
                </Col>
                <Col span={6}>
                  <strong>Total Events:</strong> {fullTankBreakdownData.length} 
                  ({fullTankBreakdownData.filter(r => {
                    const ft = (r['Full Tank'] || '').trim().toLowerCase();
                    return ft === 'true' || ft === '1';
                  }).length} full tank)
                </Col>
                <Col span={6}>
                  <strong>Average Rate:</strong> {selectedRecord.fullTankAvgConsumptionRate ? `${selectedRecord.fullTankAvgConsumptionRate.toFixed(2)} ${selectedRecord.fullTankAvgConsumptionRateUnit || ''}` : 'N/A'}
                </Col>
              </Row>
            </div>
            <Table
              columns={(() => {
                // Helper function to check if a column has data
                const hasData = (checkFn) => {
                  return fullTankBreakdownData.some(record => {
                    const result = checkFn(record);
                    return result !== '-' && result !== 'N/A' && result !== null && result !== undefined && result !== '';
                  });
                };
                
                // Helper to get KWH value from various field names
                const getKWHValue = (record, fieldName) => {
                  return getNumericValue(record, fieldName) || 
                         getNumericValue(record, fieldName.replace('KWH', 'KwH')) ||
                         getNumericValue(record, fieldName.replace('KWH', 'kwh'));
                };
                
                // Define all possible columns
                const allColumns = [
                {
                  title: 'Date',
                  key: 'date',
                  width: 120,
                  fixed: 'left',
                  render: (text, record) => {
                    const date = getDateFromFullTankRow(record);
                    return date ? date.format('DD/MM/YYYY') : (record['Date'] || 'N/A');
                  },
                  sorter: (a, b) => {
                    const dateA = getDateFromFullTankRow(a);
                    const dateB = getDateFromFullTankRow(b);
                    if (!dateA || !dateB) return 0;
                    return dateA.valueOf() - dateB.valueOf();
                    },
                    alwaysShow: true
                },
                {
                  title: 'Time',
                  key: 'time',
                  width: 100,
                  render: (text, record) => {
                    const dateStr = (record['Date'] || '').trim();
                    if (dateStr && dateStr.includes('_')) {
                      const timePart = dateStr.split('_')[1];
                      return timePart || '-';
                    }
                    return '-';
                    },
                    alwaysShow: true
                },
                {
                  title: 'Full Tank',
                  key: 'fullTank',
                  width: 100,
                  fixed: 'left',
                  render: (text, record) => {
                    const fullTank = (record['Full Tank'] || '').trim().toLowerCase();
                    const isFullTank = fullTank === 'true' || fullTank === '1';
                    return (
                      <Tag color={isFullTank ? 'green' : 'default'}>
                        {isFullTank ? 'Yes' : 'No'}
                      </Tag>
                    );
                  },
                  alwaysShow: true,
                  sorter: (a, b) => {
                    const aFullTank = ((a['Full Tank'] || '').trim().toLowerCase() === 'true' || (a['Full Tank'] || '').trim().toLowerCase() === '1');
                    const bFullTank = ((b['Full Tank'] || '').trim().toLowerCase() === 'true' || (b['Full Tank'] || '').trim().toLowerCase() === '1');
                    return aFullTank === bFullTank ? 0 : (aFullTank ? 1 : -1);
                  }
                },
                {
                  title: 'Voucher',
                  key: 'voucher',
                  width: 120,
                    render: (text, record) => record['Voucher'] || '-',
                    hasData: () => hasData(record => record['Voucher'])
                },
                {
                  title: 'Quantity Refilled (ltrs)',
                  key: 'quantityRefilled',
                  width: 160,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Quantity Refilled');
                    return value > 0 ? value.toFixed(2) : '-';
                    },
                    alwaysShow: true
                },
                {
                  title: 'Quantity Consumed (ltrs)',
                  key: 'quantityConsumed',
                  width: 170,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Quantity Consumed');
                    return value > 0 ? value.toFixed(2) : '-';
                    },
                    alwaysShow: true
                },
                {
                  title: 'KMS Reading',
                  key: 'kmsReading',
                  width: 130,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'KMS Reading');
                    return value > 0 ? value.toFixed(2) : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'KMS Reading');
                      return value > 0 ? value.toFixed(2) : '-';
                    })
                },
                {
                  title: 'HOURS Reading',
                  key: 'hoursReading',
                  width: 140,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'HOURS Reading');
                    return value > 0 ? value.toFixed(2) : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'HOURS Reading');
                      return value > 0 ? value.toFixed(2) : '-';
                    })
                  },
                  {
                    title: 'KWH Reading',
                    key: 'kwhReading',
                    width: 140,
                    render: (text, record) => {
                      const value = getKWHValue(record, 'KWH Reading');
                      return value > 0 ? value.toFixed(2) : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getKWHValue(record, 'KWH Reading');
                      return value > 0 ? value.toFixed(2) : '-';
                    })
                },
                {
                  title: 'KMS Consumed',
                  key: 'kmsConsumed',
                  width: 140,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'KMS Consumed');
                    return value > 0 ? value.toFixed(2) : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'KMS Consumed');
                      return value > 0 ? value.toFixed(2) : '-';
                    })
                },
                {
                  title: 'HOURS Consumed',
                  key: 'hoursConsumed',
                  width: 150,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'HOURS Consumed');
                    return value > 0 ? value.toFixed(2) : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'HOURS Consumed');
                      return value > 0 ? value.toFixed(2) : '-';
                    })
                  },
                  {
                    title: 'KWH Consumed',
                    key: 'kwhConsumed',
                    width: 150,
                    render: (text, record) => {
                      const value = getKWHValue(record, 'KWH Consumed');
                      return value > 0 ? value.toFixed(2) : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getKWHValue(record, 'KWH Consumed');
                      return value > 0 ? value.toFixed(2) : '-';
                    })
                },
                {
                  title: 'KMS/ Qty',
                  key: 'kmsPerQty',
                  width: 120,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'KMS/ Qty');
                    return value > 0 ? `${value.toFixed(2)} KMS/Ltr` : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'KMS/ Qty');
                      return value > 0 ? `${value.toFixed(2)} KMS/Ltr` : '-';
                    })
                },
                {
                  title: 'Qty/ KMS',
                  key: 'qtyPerKms',
                  width: 120,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Qty/ KMS');
                    return value > 0 ? `${value.toFixed(2)} Ltr/KMS` : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'Qty/ KMS');
                      return value > 0 ? `${value.toFixed(2)} Ltr/KMS` : '-';
                    })
                },
                {
                  title: 'HOURS/ Qty',
                  key: 'hoursPerQty',
                  width: 130,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'HOURS/ Qty');
                    return value > 0 ? `${value.toFixed(2)} Hrs/Ltr` : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'HOURS/ Qty');
                      return value > 0 ? `${value.toFixed(2)} Hrs/Ltr` : '-';
                    })
                },
                {
                  title: 'Qty/ HOURS',
                  key: 'qtyPerHours',
                  width: 130,
                  render: (text, record) => {
                    const value = getNumericValue(record, 'Qty/ HOURS');
                    return value > 0 ? `${value.toFixed(2)} Ltr/Hrs` : '-';
                    },
                    hasData: () => hasData(record => {
                      const value = getNumericValue(record, 'Qty/ HOURS');
                      return value > 0 ? `${value.toFixed(2)} Ltr/Hrs` : '-';
                    })
                  },
                  {
                    title: 'KWH/ Qty',
                    key: 'kwhPerQty',
                    width: 130,
                    render: (text, record) => {
                      const kwhPerQty = getKWHValue(record, 'KWH/ Qty') || 
                                       getKWHValue(record, 'KWH/L') ||
                                       getKWHValue(record, 'KWH/Ltr');
                      return kwhPerQty > 0 ? `${kwhPerQty.toFixed(2)} KWH/Ltr` : '-';
                    },
                    hasData: () => hasData(record => {
                      const kwhPerQty = getKWHValue(record, 'KWH/ Qty') || 
                                       getKWHValue(record, 'KWH/L') ||
                                       getKWHValue(record, 'KWH/Ltr');
                      return kwhPerQty > 0 ? `${kwhPerQty.toFixed(2)} KWH/Ltr` : '-';
                    })
                },
                {
                  title: 'Consumption Rate',
                  key: 'consumptionRate',
                  width: 150,
                  render: (text, record) => {
                    const kmsPerQty = getNumericValue(record, 'KMS/ Qty');
                    const qtyPerHours = getNumericValue(record, 'Qty/ HOURS');
                    const qtyPerKms = getNumericValue(record, 'Qty/ KMS');
                    const hoursPerQty = getNumericValue(record, 'HOURS/ Qty');
                    
                    if (kmsPerQty > 0) {
                      return `${kmsPerQty.toFixed(2)} KMS/Ltr`;
                    } else if (qtyPerHours > 0) {
                      return `${qtyPerHours.toFixed(2)} Ltr/Hrs`;
                    } else if (qtyPerKms > 0) {
                      return `${qtyPerKms.toFixed(2)} Ltr/KMS`;
                    } else if (hoursPerQty > 0) {
                      return `${hoursPerQty.toFixed(2)} Hrs/Ltr`;
                    }
                    return '-';
                    },
                    hasData: () => hasData(record => {
                      const kmsPerQty = getNumericValue(record, 'KMS/ Qty');
                      const qtyPerHours = getNumericValue(record, 'Qty/ HOURS');
                      const qtyPerKms = getNumericValue(record, 'Qty/ KMS');
                      const hoursPerQty = getNumericValue(record, 'HOURS/ Qty');
                      return kmsPerQty > 0 || qtyPerHours > 0 || qtyPerKms > 0 || hoursPerQty > 0;
                    })
                },
                {
                  title: 'Fuel Type',
                  key: 'fuelType',
                  width: 100,
                    render: (text, record) => record['Fuel Type'] || '-',
                    hasData: () => hasData(record => record['Fuel Type'])
                },
                {
                  title: 'Operator',
                  key: 'operator',
                  width: 120,
                    render: (text, record) => record['Operator'] || '-',
                    hasData: () => hasData(record => record['Operator'])
                },
                {
                  title: 'Location',
                  key: 'location',
                  width: 150,
                    render: (text, record) => record['Location'] || '-',
                    hasData: () => hasData(record => record['Location'])
                }
                ];
                
                // Filter columns to only show those with data or alwaysShow flag
                return allColumns.filter(col => col.alwaysShow || (col.hasData && col.hasData()));
              })()}
              dataSource={fullTankBreakdownData}
              rowKey={(record, index) => `fulltank_${index}_${getDateFromFullTankRow(record)?.format('YYYY-MM-DD') || index}`}
              scroll={{ x: 1500, y: 500 }}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} events`
              }}
              size="small"
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default IoTSummaryReport;

